/**
 * The visit page's words — every one of them from a board in
 * `design/mockups/50-visit-page/`, or authorised by `docs/visit-page.md`
 * §27.7, and nowhere else in `client/src/visit/**` (⛔ rule 3; the strings
 * checker holds the line). Values — references, names, money, the count —
 * arrive from the server and are only said here.
 *
 * Organised by screen, then by component, in the order the boards draw them:
 * `COMMON` (words several screens draw — one entry each), `CANCEL_VISIT`
 * (Board 17 F, PR 1's — first because the header's ⋯ names it), `HEADER`,
 * `TABS`, `OVERVIEW`, `RAIL`, `MATERIALS`, `SWAP`, `ORDER`, `EQUIPMENT`,
 * `UNIT_PICKER`, `ADD_UNIT`, `RENTALS`, `RENT`, `TIME`, `EDIT_PUNCH`,
 * `ADD_TIME`, `MONEY`, `RECEIPTS`, `FOR_CREW`, `CREW_NOTE`, `CREW_PREVIEW`,
 * `FROM_CREW`, `LIGHTBOX`, `DAY_SHEET`, `ADD_DAY`, `MOVE_DAY`, `CLOSE`,
 * `CLOSE_OUT_FOR_CREW`, `STATES`, `ONE_DAY`, `TM_BILLED`, `SERIES`, `HOW_FAR`,
 * `PHONE` (the iPad draws the desktop's words).
 *
 * Rules for a caller:
 * - A string with data in it is a function here; its fixed words are this
 *   file's and its data are parameters. Never assemble a sentence in a
 *   component — call the function.
 * - Parameters are strings exactly as the board prints them — `$8,888`,
 *   `+$19.47`, `−0.2`, `35.9`, `76`, `6:45`, `Fri Oct 23`, `Wed 5:06 PM` —
 *   made from the server's values by `lib/format.ts`, never computed here.
 * - Maps keyed by `shared/src/visit-page` unions (`MaterialState`,
 *   `SwapReason`, `PhotoTag` …) hold the board's word for each key the
 *   boards draw. A key no board draws has no word here — ask, never invent.
 */
import { formatMoneyText } from '../lib/format.js'

/**
 * A sentence set after a colon, as the boards print one (*Note for the crew:
 * hitch is a 2 in ball — take the F-250*): its first word in lower case —
 * unless that word is a single letter or reads like a code (*KR-4471*) — and
 * no closing full stop.
 */
function afterColon(text: string): string {
  const trimmed = text.trim().replace(/\.$/, '')
  const firstWord = trimmed.split(/\s/, 1)[0] ?? ''
  const plain = firstWord.length > 1 && firstWord.slice(1) === firstWord.slice(1).toLowerCase()
  return plain ? `${trimmed.charAt(0).toLowerCase()}${trimmed.slice(1)}` : trimmed
}

/** *A, B and C* — the parts of a list the boards draw that way. */
function allButLast(items: readonly string[]): string {
  return items.slice(0, -1).join(', ')
}
function lastOf(items: readonly string[]): string {
  return items[items.length - 1] ?? ''
}

// ===========================================================================
// Words more than one screen draws — one entry each
// ===========================================================================

export const COMMON = {
  today: 'today',
  tomorrow: 'tomorrow',
  /** The chip on everything tinted and locked (`surface-internal`). */
  internal: 'Internal',
  cancel: 'Cancel',
  save: 'Save',
  close: 'Close',
  edit: 'Edit',
  undo: 'Undo',
  review: 'Review',
  order: 'Order',
  swap: 'Swap',
  seeAll: 'See all',
  preview: 'Preview',
  navigate: 'Navigate',
  call: 'Call',
  text: 'Text',
  why: 'Why',
  when: 'When',
  vendor: 'Vendor',
  type: 'Type',
  needed: 'Needed',
  hours: 'Hours',
  crew: 'Crew',
  /** A label — About on a one-day visit, Add a day, Add time, the one-day card. */
  day: 'Day',
  days: 'Days',
  thisVisit: 'This visit',
  job: 'Job',
  property: 'Property',
  contact: 'Contact',
  /** The day chips and the material groups — *Closed out*, *On site*, *Ready*. */
  closedOut: 'Closed out',
  onSite: 'On site',
  ready: 'Ready',
  scheduled: 'Scheduled',
  skipped: 'Skipped',
  planned: 'planned',
  plannedLabel: 'Planned',
  soFar: 'so far',
  soFarLabel: 'So far',
  projected: 'projected',
  projectedLabel: 'Projected',
  estimate: 'estimate',
  estimateLabel: 'Estimate',
  actual: 'actual',
  actualLabel: 'Actual',
  vsEstimate: 'vs estimate',
  category: 'Category',
  margin: 'margin',
  marginLabel: 'Margin',
  cost: 'cost',
  costLabel: 'Cost',
  earns: 'Earns',
  labor: 'Labor',
  fees: 'Fees',
  totalCost: 'Total cost',
  closeOut: 'Close-out',
  equipmentHours: 'Equipment hours',
  loadList: 'Load list',
  addADay: 'Add a day',
  howItGetsThere: 'How it gets there',
  crewPicksUp: 'Crew picks up',
  picksUp: 'Picks up',
  noteForTheCrew: 'Note for the crew',
  addANoteForTheCrew: 'Add a note for the crew',
  tellTheCrewNow: 'Tell the crew now',
  companyEquipment: 'Company equipment',
  /** Time's legend and a punch's hours — *2.7 h* **on this visit**. */
  onThisVisit: 'on this visit',
  onTheClock: 'on the clock',
  lunch: 'lunch',
  readyForPickup: 'Ready for pickup',
  boughtInTheField: 'Bought in the field',
  used: 'Used',
  estCost: 'Est. cost',
  /** The grey tag on a PO the office noted while purchasing is off (Board 18). */
  noted: 'Noted',
  /** *Fixed price* — the billing tag and the swap sheet's internal card. */
  fixedPrice: 'Fixed price',
  timeAndMaterials: 'Time & materials',
  perVisit: 'Per visit',
  /** On a rail row and a series arrow — *V-1182 · Tue Oct 20 · closed*. */
  closed: 'closed',
  /** §27.7 — a cancelled row of the rail's Visits card. */
  cancelled: 'cancelled',
  /** The place a punch or a stop names — *in · yard*, *in 6:31 at the yard*. */
  yard: 'yard',
  theYard: 'the yard',
  /** The few number words the boards draw (*three days*, *the second of three days*). */
  numberWords: { 3: 'three' } as Record<number, string>,
  ordinalWords: { 2: 'second' } as Record<number, string>,
  /** The rest of §7.4's and §15.1's number words, for visits of other lengths (`contractText`). */
  moreNumberWords: {
    1: 'one', 2: 'two', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight', 9: 'nine', 10: 'ten',
  } as Record<number, string>,
  moreOrdinalWords: {
    1: 'first', 3: 'third', 4: 'fourth', 5: 'fifth', 6: 'sixth', 7: 'seventh', 8: 'eighth', 9: 'ninth', 10: 'tenth',
  } as Record<number, string>,

  /** *Day 2*. */
  dayN: (n: string) => `Day ${n}`,
  /** *Day 2 of 3*. */
  dayOf: (n: string, count: string) => `Day ${n} of ${count}`,
  /** *Day 3 · Fri Oct 23* · *Day 1 · Wed*. */
  dayDate: (n: string, date: string) => `Day ${n} · ${date}`,
  /** *Day 2 · Thu Oct 22 · today*. */
  dayDateRelative: (n: string, date: string, relative: string) => `Day ${n} · ${date} · ${relative}`,
  /** *Thu Oct 22 · today* · *Fri Oct 23 · tomorrow* · *Thu · today*. */
  dateRelative: (date: string, relative: string) => `${date} · ${relative}`,
  /** *Wed Oct 21, 1:18 PM · Day 1* · *Wed 9:40 AM · Day 1*. */
  atDay: (when: string, n: string) => `${when} · Day ${n}`,
  /** A title's count, drawn after it — *· 3*. */
  count: (n: string) => `· ${n}`,
  /** Two parts the board sets side by side — *Hardscape 2 · Marco Diaz*. */
  dot: (a: string, b: string) => `${a} · ${b}`,
  /** *A and B*. */
  and: (a: string, b: string) => `${a} and ${b}`,
  /** *5 of 7* · *12 of 18 t*. */
  of: (part: string, whole: string) => `${part} of ${whole}`,
  /** *35.9 h* · *+1.0 h*. */
  h: (hours: string) => `${hours} h`,
  /** The unit after an hours box (*8 h*), and in a rate (*$32 / h*). */
  hourUnit: 'h',
  /** *−0.2 pts*. */
  pts: (signed: string) => `${signed} pts`,
  /** *$32 / h* · *$34.00 / bag*. */
  per: (amount: string, unit: string) => `${amount} / ${unit}`,
  /** *9 bags* · *420 sq ft*. */
  qty: (qty: string, unit: string) => `${qty} ${unit}`,
  /** *Open PO-1052* · *Open J-3271*. */
  open: (reference: string) => `Open ${reference}`,
  /** *of 76.0 h estimated* · *of $8,869 estimated*. */
  ofEstimated: (estimate: string) => `of ${estimate} estimated`,
  /** *estimated 39.9%* · *estimated $1,130.00*. */
  estimated: (estimate: string) => `estimated ${estimate}`,
  /** *of 27 planned*. */
  ofPlanned: (planned: string) => `of ${planned} planned`,
  /** *so far of 27*. */
  soFarOf: (planned: string) => `so far of ${planned}`,
  /** *22 h planned*. */
  hoursPlanned: (hours: string) => `${hours} h planned`,
  /** *8.0 h used*. */
  hoursUsed: (hours: string) => `${hours} h used`,
  /** *on site since 6:58*. */
  onSiteSince: (time: string) => `on site since ${time}`,
  /** *7:00 start*. */
  start: (time: string) => `${time} start`,
  /** *from Apr 15*. */
  from: (date: string) => `from ${date}`,
  /** *Ready for Friday*. */
  readyFor: (weekday: string) => `Ready for ${weekday}`,
  /** *Prep 5 of 7*. */
  prep: (ready: string, total: string) => `Prep ${ready} of ${total}`,
  /** *Capital Hardscape Supply has 3*. */
  vendorHas: (vendor: string, n: string) => `${vendor} has ${n}`,
  /** *Add to PO-1051*. */
  addTo: (po: string) => `Add to ${po}`,
  /** *Fri Oct 23 · from Q-2184* · *J-3271 · from Q-2184*. */
  fromQuote: (what: string, quote: string) => `${what} · from ${quote}`,
  /** *29 – 30 · through Nov 19*. */
  through: (first: string, last: string, date: string) => `${first} – ${last} · through ${date}`,
  /** *Wed Oct 21 – Fri Oct 23*. */
  range: (first: string, last: string) => `${first} – ${last}`,
  /** *“Excavated, fabric down …”*. */
  quoted: (text: string) => `“${text}”`,
  /** *A, B and C* — a list of names or things. */
  list: (items: readonly string[]) => (items.length > 1 ? `${allButLast(items)} and ${lastOf(items)}` : lastOf(items)),
  /** *Mini skid steer, plate compactor, dump trailer, wet saw* — §27.7's type names line. */
  commaList: (items: readonly string[]) => items.join(', '),
} as const

// ===========================================================================
// Cancelling a visit that came from a quote — Board 17 F, §26.7 (PR 1; here,
// before the header, because the header's ⋯ names it)
// ===========================================================================

export const CANCEL_VISIT = {
  title: 'Cancel the visit',
  sub: 'a visit that came from an approved quote',
  /** *V-1188 · from Q-2184, quote visit 2 · $1,230.00* */
  line: (reference: string, quote: string, quoteVisit: number, amount: string) =>
    `${reference} · from ${quote}, quote visit ${quoteVisit} · ${formatMoneyText(amount)}`,
  /** The mark's band on a visit with no day yet. */
  why: 'Why',
  staysOn: (job: string) => `It stays on ${job} as cancelled`,
  stillCounts: (quote: string, present: number, expected: number) =>
    `and still counts for ${quote} — the check reads ${present} of ${expected}. Its money comes off what the job expects only with a change order.`,
  tell: (name: string) => `Tell ${name}`,
  tellWhy: '— a change order may be needed',
  keep: 'Keep it',
  cancel: 'Cancel the visit',
  /** The ✕'s name for a screen reader — standard chrome (⛔: *closes, as Cancel*). */
  close: 'Close',
} as const

// ===========================================================================
// The header — Board 1 (and every board's), §7.2, §17.3
// ===========================================================================

export const HEADER = {
  /** The breadcrumb's first part, and the phone's back. */
  breadcrumb: 'Visits',
  /** The status tag — §17.1. *Completed* is drawn on no board. */
  status: {
    unscheduled: 'Unscheduled',
    scheduled: COMMON.scheduled,
    in_progress: 'In progress',
    /** §17.1 (contractText). */
    completed: 'Completed',
    closed: 'Closed',
    on_hold: 'On hold',
    cancelled: 'Cancelled',
    skipped: COMMON.skipped,
  },
  /** The billing tag. */
  billing: {
    fixed: COMMON.fixedPrice,
    time_materials: COMMON.timeAndMaterials,
    per_visit: COMMON.perVisit,
  },
  meta: {
    /** *Wed Oct 21 – Fri Oct 23 · Day 2 of 3*. */
    daysToday: (range: string, n: string, count: string) => `${range} · Day ${n} of ${count}`,
    /** *Oct 21 – 23 · 3 days*. */
    days: (range: string, count: string) => `${range} · ${count} days`,
    /** *Tue Oct 20 · 1 day*. */
    oneDay: (date: string) => `${date} · 1 day`,
    /** *Thursdays Apr 30 – Nov 19 · 26 of 30* — `weekday` is the day's name, *Thursday*. */
    series: (weekday: string, range: string, n: string, count: string) => `${weekday}s ${range} · ${n} of ${count}`,
    /** *Paul Hess · property manager*. */
    contact: COMMON.dot,
    /** *Hardscape 2 · Marco Diaz*. */
    crew: COMMON.dot,
    directions: 'Directions',
  },
  pills: {
    /** *35.9 h* **of 76 so far** · series *of 6.5 so far*. */
    hoursSoFar: (planned: string) => `of ${planned} so far`,
    /** Closed — *78.0 h* **of 76**. */
    hoursOf: (planned: string) => `of ${planned}`,
    money: {
      projected_cost: 'projected cost',
      cost: COMMON.cost,
      this_visit: 'this visit',
      to_bill: 'to bill',
    },
    margin: {
      projected: 'projected margin',
      final: COMMON.margin,
      series: 'series margin',
    },
  },
  /** The ghost button — §17.3. */
  secondary: {
    message_crew: 'Message crew',
    edit_plan: 'Edit plan',
    open_series: 'Open the series',
    message: 'Message',
    open_job: 'Open the job',
  },
  /** The green button — §17.3. */
  primary: {
    review_close: 'Review & close',
    schedule: 'Schedule',
    reschedule: 'Reschedule',
    take_off_hold: 'Take off hold',
  },
  /** ⋯ — only what a board draws; *Put on hold · Print the crew sheet · Copy link* are on none. */
  menu: {
    add_day: COMMON.addADay,
    /** §7.2 (contractText) — the three the ⋯ holds that no board draws. */
    put_on_hold: 'Put on hold',
    print_crew_sheet: 'Print the crew sheet',
    copy_link: 'Copy link',
    cancel_visit: CANCEL_VISIT.title,
  },
  /** The lock banner on a closed visit — Board 12 C, Board 13 A. */
  closed: {
    /** *Closed by Jen Hollis · Fri 5:54 PM.* */
    by: (name: string, when: string) => `Closed by ${name} · ${when}.`,
    /** *The numbers are locked into J-3271. A fix is an adjustment, with a reason.* */
    locked: (job: string) => `The numbers are locked into ${job}. A fix is an adjustment, with a reason.`,
    /** Board 13 A — *The numbers are locked into J-3288.* */
    lockedShort: (job: string) => `The numbers are locked into ${job}.`,
    adjust: 'Adjust',
  },
} as const

// ===========================================================================
// The tabs — Board 1, do-not-substitute 3 (Overview · Activity · Files ·
// History are the record shell's own words)
// ===========================================================================

export const TABS = {
  materials: 'Materials',
  equipment: 'Equipment',
  time: 'Time',
  money: 'Money',
  'for-crew': 'For the crew',
  'from-crew': 'From the crew',
} as const

// ===========================================================================
// The Overview — Board 1, §7.4 – §7.8
// ===========================================================================

export const OVERVIEW = {
  /** **Days · 3** — §7.4. */
  days: {
    title: COMMON.days,
    /** *one visit, three days on site · click a day to open it* — `count` is a number word (`COMMON.numberWords`). */
    sub: (count: string) => `one visit, ${count} days on site · click a day to open it`,
    openSchedule: 'Open the schedule',
    /** The big hours — *28.0 h* **of 27 planned** · *7.9 h* **so far of 27** · *22 h* **planned**. */
    ofPlanned: COMMON.ofPlanned,
    soFarOf: COMMON.soFarOf,
    planned: COMMON.planned,
    chip: {
      closed_out: COMMON.closedOut,
      on_site: COMMON.onSite,
      prep: COMMON.prep,
      ready: COMMON.ready,
      scheduled: COMMON.scheduled,
      skipped: COMMON.skipped,
    },
    /** *on site since 6:58* · *7:00 start*. */
    onSiteSince: COMMON.onSiteSince,
    start: COMMON.start,
    /** *+1.0 h · old footing*. */
    over: (hours: string, reason: string) => `${hours} h · ${reason}`,
    addDay: COMMON.addADay,
    orMoveOne: 'or move one',
  },
  /** **Estimate vs actual** — §7.6 (Board 1 A, B; Board 13 A; Board 14). */
  estimate: {
    title: 'Estimate vs actual',
    sub: 'projected = what is done, plus the rest of the plan at today’s prices',
    /** Board 1 B — a role without *Costs & margins*. */
    hoursOnly: 'hours only — this role doesn’t see costs',
    /** Board 13 A. */
    final: 'final — the visit is closed',
    /** Board 14. */
    series: 'this visit, and the series so far',
    /**
     * §4.2, D23 — a visit approved before plan lines has no estimate, and the
     * card says so in place of every *of … estimated* (never *of 0.0 h
     * estimated*; walkthrough Oct 30, §9). Authorised by the lead as contract
     * text (`visit-authorised-strings.json` `contractText`).
     */
    noEstimate: 'No estimate — approved before plan lines',
    moneyTab: 'Money tab',
    hours: COMMON.hours,
    cost: COMMON.costLabel,
    margin: COMMON.marginLabel,
    thisVisit: COMMON.thisVisit,
    soFar: COMMON.soFar,
    actual: COMMON.actual,
    projected: COMMON.projected,
    projectedLabel: COMMON.projectedLabel,
    /** *of 76.0 h estimated* · *of $8,869 estimated*. */
    ofEstimated: COMMON.ofEstimated,
    /** *estimated 37.8% on a $14,250 fixed price*. */
    estimatedOnFixed: (percent: string, price: string) => `estimated ${percent} on a ${price} fixed price`,
    /** *estimated 39.9%*. */
    estimated: COMMON.estimated,
    earns: COMMON.earns,
    /** Board 13 A — *Final · under the estimate*. */
    finalUnder: 'Final · under the estimate',
    /** Board 13 A — *Final · on $1,043 billed*. */
    finalOnBilled: (billed: string) => `Final · on ${billed} billed`,
    /** Board 14 — *of 6.5 h · on site since 7:56*. */
    ofHoursOnSiteSince: (hours: string, time: string) => `of ${hours} h · on site since ${time}`,
    /** Board 14 — a zero difference. */
    asPlanned: 'as planned',
    /** Board 14 — SERIES TO DATE. */
    seriesToDate: 'Series to date',
    /** *estimated 42.0% · 24 visits closed*. */
    seriesEstimated: (percent: string, closed: string) => `estimated ${percent} · ${closed} visits closed`,
    billed: 'Billed',
    /** *of $11,850 · 24 × $395.00*. */
    ofBilled: (total: string, count: string, each: string) => `of ${total} · ${count} × ${each}`,
    /** The legend under the KPIs. */
    legend: { soFar: COMMON.soFar, stillToCome: 'still to come', estimate: COMMON.estimate },
  },
  /** **Off the plan · 4** — §6.4. */
  offPlan: {
    title: (n: string) => `Off the plan · ${n}`,
    sub: 'what moved the projection, biggest first',
    /** *Wall caps swapped to Belgard Bullnose, Pewter*. */
    swapTitle: (line: string, to: string) => `${line} swapped to ${to}`,
    /**
     * *Mini-Creta backordered · fixed price, so the visit pays the difference ·
     * Jen Hollis, Tue* — `reason` from `SWAP.reasonInSentence`.
     */
    swapLine: (from: string, reason: string, name: string, day: string) =>
      `${from} ${reason} · fixed price, so the visit pays the difference · ${name}, ${day}`,
    /** *Hilltop Hardware receipt against the adhesive’s plan*. */
    receiptTitle: (vendor: string, noun: string) => `${vendor} receipt against the ${noun}’s plan`,
    /** *$64.52 against $45.00 · a masonry blade too · to review*. */
    receiptLine: (spent: string, planned: string, also: string) => `${spent} against ${planned} · ${also} too · to review`,
    /** *Dump fee, Day 1*. */
    feeTitle: (name: string, n: string) => `${name}, Day ${n}`,
    /** *Cumberland Transfer Station $91.40 against $85.00 planned*. */
    feeLine: (vendor: string, spent: string, planned: string) => `${vendor} ${spent} against ${planned} planned`,
    labor: COMMON.labor,
    /** *Day 1 ran 1.0 h over (an old footing) · the crew’s rates average $33.00 against the $34.00 estimate*. */
    laborLine: (n: string, hours: string, reason: string, crewRate: string, estimateRate: string) =>
      `Day ${n} ran ${hours} h over (${reason}) · the crew’s rates average ${crewRate} against the ${estimateRate} estimate`,
    /** Board 1 B — *Day 1 ran 1.0 h over*. */
    ranOver: (n: string, hours: string) => `Day ${n} ran ${hours} h over`,
    total: 'Projected against the estimate',
  },
  /** **Ready for Friday** — §7.7. */
  ready: {
    title: COMMON.readyFor,
    /** *Day 3 · Fri Oct 23 · 7:00 start*. */
    sub: (n: string, date: string, time: string) => `Day ${n} · ${date} · ${time} start`,
    /** *5 of 7 ready*. */
    count: (ready: string, total: string) => `${ready} of ${total} ready`,
    loadList: COMMON.loadList,
    order: COMMON.order,
    /** *Fire pit kit, Belgard Weston 46 in — not ordered*. */
    notOrdered: (name: string) => `${name} — not ordered`,
    /** *needed by 7:00 Fri · Capital Hardscape Supply has 3*. */
    neededBy: (time: string, day: string, vendor: string, stock: string) => `needed by ${time} ${day} · ${vendor} has ${stock}`,
    /** No vendor stock kept yet (round 7, §25 item 6) — *needed by 7:00 Fri · Capital Hardscape Supply*. */
    neededByVendor: (time: string, day: string, vendor: string) => `needed by ${time} ${day} · ${vendor}`,
    /** *Wall caps, 24 — on the way to the yard*. */
    onTheWayToYard: (name: string, qty: string) => `${name}, ${qty} — on the way to the yard`,
    /** *PO-1052 · Capital Hardscape Supply delivers today, about 2 PM*. */
    delivers: (po: string, vendor: string, when: string, time: string) => `${po} · ${vendor} delivers ${when}, about ${time}`,
    /** *Pick up on the way — Gator poly sand, 9 bags*. */
    pickUp: (name: string, qty: string) => `Pick up on the way — ${name}, ${qty}`,
    /** *Load at the yard — Snap edge restraint*. */
    load: (name: string) => `Load at the yard — ${name}`,
    /** *held for this visit · with the caps once they land*. */
    heldWith: (noun: string) => `held for this visit · with the ${noun} once they land`,
    /** *Rented — Towable concrete mixer*. */
    rented: (name: string) => `Rented — ${name}`,
    /** *Keystone Equipment Rental · Jesse Lutz picks up 7:00 · KR-4471*. */
    rentedLine: (vendor: string, name: string, time: string, confirmation: string) =>
      `${vendor} · ${name} picks up ${time} · ${confirmation}`,
    bookIt: 'Book it',
    companyEquipment: COMMON.companyEquipment,
    /** *MSS-03 and PC-07 stay on site*. */
    staysOnSite: (units: string) => `${units} stay on site`,
    /** *a wet saw picked at 6:30 (2 free)*. */
    decidePicked: (type: string, time: string, free: string) => `a ${type} picked at ${time} (${free} free)`,
    /** *Crew — Hardscape 2*. */
    crew: (crew: string) => `Crew — ${crew}`,
    /** Phone — *All 7*. */
    all: (n: string) => `All ${n}`,
  },
  /** **For the crew · 9** — §7.8. */
  forCrewCard: {
    title: TABS['for-crew'],
    sub: 'on the crew’s iPad',
    preview: COMMON.preview,
    /** *3 files*. */
    files: (n: string) => `${n} files`,
    /** *2 map layers*. */
    mapLayers: (n: string) => `${n} map layers`,
    add: COMMON.addANoteForTheCrew,
  },
  /** **From the crew · 15** — §7.8. */
  fromCrewCard: {
    title: TABS['from-crew'],
    sub: 'photos, notes, receipts',
    seeAll: COMMON.seeAll,
    /** The newest photo's tag. */
    today: 'Today',
    /** *+7*. */
    more: (n: string) => `+${n}`,
    /** Board 13 A — *Tue 4:06 PM · close-out*. */
    atCloseOut: (when: string) => `${when} · close-out`,
    /** *· Sam Okoro · paid with own money* — `paidWith` from `RECEIPTS.paidWithInSentence`. */
    receiptLine: (name: string, paidWith: string) => `· ${name} · paid with ${paidWith}`,
    review: COMMON.review,
  },
} as const

// ===========================================================================
// The rail (340) — Board 1, §7.3
// ===========================================================================

export const RAIL = {
  about: {
    title: 'About',
    edit: COMMON.edit,
    visit: 'Visit',
    job: COMMON.job,
    days: COMMON.days,
    day: COMMON.day,
    crew: COMMON.crew,
    division: 'Division',
    billing: 'Billing',
    planned: COMMON.plannedLabel,
    leavesFrom: 'Leaves from',
    /** *V-1160 · 26 of 30*. */
    seriesVisit: (reference: string, n: string, count: string) => `${reference} · ${n} of ${count}`,
    /** *Hardscape 2 · Marco Diaz, lead*. */
    crewLead: (crew: string, lead: string) => `${crew} · ${lead}, lead`,
    /** *Fixed price · $14,250* · *Time & materials · $58.00 / h*. */
    billingLine: (billing: string, price: string) => `${billing} · ${price}`,
    /** *Per visit · $395.00 · invoiced monthly*. */
    billingMonthly: (billing: string, price: string) => `${billing} · ${price} · invoiced monthly`,
    /** *76.0 h · from Q-2184*. */
    plannedFrom: (hours: string, quote: string) => `${hours} h · from ${quote}`,
  },
  /** **Visits on J-3271 · 2** — §7.3, revision 2. */
  jobVisits: {
    title: (job: string) => `Visits on ${job}`,
    link: 'Job’s visits',
    thisVisit: COMMON.thisVisit,
    /** After **This visit** — *· V-1187 · Oct 21 – 23 · Day 2 of 3*. */
    afterThisVisit: (line: string) => `· ${line}`,
    /** *V-1187 · Oct 21 – 23 · Day 2 of 3*. */
    lineToday: (reference: string, range: string, n: string, count: string) => `${reference} · ${range} · Day ${n} of ${count}`,
    /** *V-1188 · not scheduled · from Apr 15 · 6.0 h*. */
    lineUnscheduled: (reference: string, from: string, hours: string) => `${reference} · not scheduled · from ${from} · ${hours} h`,
    /** *V-1183 · Tue Nov 17 · 8.0 h planned*. */
    linePlanned: (reference: string, when: string, hours: string) => `${reference} · ${when} · ${hours} h planned`,
    /** *V-1160 · today · 3.5 h so far*. */
    lineSoFar: (reference: string, when: string, hours: string) => `${reference} · ${when} · ${hours} h so far`,
    /** *V-1182 · Tue Oct 20 · closed* · *V-1159 · Thu Oct 15 · 6.6 h · closed*. */
    lineClosed: (reference: string, when: string) => `${reference} · ${when} · closed`,
    lineClosedHours: (reference: string, when: string, hours: string) => `${reference} · ${when} · ${hours} h · closed`,
    /** §27.7 — a cancelled visit's row. */
    cancelled: COMMON.cancelled,
    /** *1 – 24 · 23 closed, 1 skipped*. */
    fold: (first: string, last: string, closed: string, skipped: string) => `${first} – ${last} · ${closed} closed, ${skipped} skipped`,
    /** *29 – 30 · through Nov 19*. */
    foldThrough: COMMON.through,
    /** *Every visit on Q-2184 is here · 2 of 2*. */
    checkWhole: (quote: string, present: string, expected: string) => `Every visit on ${quote} is here · ${present} of ${expected}`,
    /** Board 17 H — *Q-2051 has 2 visits — 1 is missing.* */
    checkMissingOne: (quote: string, count: string, missing: string) => `${quote} has ${count} visits — ${missing} is missing.`,
    /** §27.7 — *<quote> has <count> visits — <n> are missing.* */
    checkMissing: (quote: string, count: string, missing: string) => `${quote} has ${count} visits — ${missing} are missing.`,
  },
  /** **People · 3**. */
  people: {
    title: 'People',
    call: COMMON.call,
    text: COMMON.text,
    /** *Customer · (717) 555-0148*. */
    customer: (phone: string) => `Customer · ${phone}`,
    /** *Crew lead · Hardscape 2*. */
    crewLead: (crew: string) => `Crew lead · ${crew}`,
    /** *Salesperson · sold Q-2184*. */
    salesperson: (quote: string) => `Salesperson · sold ${quote}`,
  },
  /** **Purchase orders · 3**. */
  purchaseOrders: {
    title: 'Purchase orders',
    link: 'Purchasing',
    /** *all with Capital Hardscape Supply*. */
    allWith: (vendor: string) => `all with ${vendor}`,
    /** Board 18 C — *all with Capital Hardscape Supply · noted by the office*. */
    allWithNoted: (vendor: string) => `all with ${vendor} · noted by the office`,
    /** The PO's state — only those a board draws. */
    status: {
      placed: 'Placed',
      ready_for_pickup: COMMON.readyForPickup,
      delivered: 'Delivered',
    },
    /** Board 18 — the grey tag on a noted PO. */
    noted: COMMON.noted,
    /** *wall caps · today*. */
    line: COMMON.dot,
  },
  /** **Close-out** — §7.3, Board 18 F. */
  closeOut: {
    title: COMMON.closeOut,
    /** *Day 1 · closed out by the crew*. */
    closedOutByCrew: (n: string) => `Day ${n} · closed out by the crew`,
    /** *Day 2 · on site now*. */
    onSiteNow: (n: string) => `Day ${n} · on site now`,
    crewClosesIt: 'the crew closes it out from the iPad',
    /** Board 18 F — today's line while the crew app can't close out. */
    officeClosesIt: 'the office closes it out for the crew',
    /** *Day 3 · Fri Oct 23*. */
    upcoming: COMMON.dayDate,
    lastDay: 'last day',
    /** Board 18 F — *Day 1 · not closed out yet*. */
    notClosedOut: (n: string) => `Day ${n} · not closed out yet`,
    /** *ended Wed 5:00 PM*. */
    ended: (when: string) => `ended ${when}`,
    closeOut: 'Close out',
    then: 'Then the office reviews and closes it.',
    /** *Review & close lists what is still open — 1 receipt to review today.* */
    stillOpen: (n: string) => `Review & close lists what is still open — ${n} receipt to review today.`,
    /** Board 13 A. */
    closedOutByTheCrew: 'Closed out by the crew',
    closedByTheOffice: 'Closed by the office',
  },
} as const

// ===========================================================================
// Materials — Boards 2, 3 A, 16 C, 18 C, §8
// ===========================================================================

export const MATERIALS = {
  views: { status: 'By status', day: 'By day' },
  find: 'Find a material',
  /** The internal chip on a fixed-price visit (§8.1). */
  fixedChip: 'Fixed price — a swap’s difference is the visit’s',
  /** §8.1's chip on a time & materials visit (contractText). */
  timeAndMaterialsChip: 'Time & materials — a swap bills at the new price',
  add: 'Add a material',
  /** *Order what’s needed · 1*. */
  orderNeeded: (n: string) => `Order what’s needed · ${n}`,
  /** By status's groups and the summary strip (§8.2). */
  groups: {
    needs_ordering: 'Needs ordering',
    on_the_way: 'On the way',
    ready: COMMON.ready,
    bought: COMMON.boughtInTheField,
    used: COMMON.used,
  },
  /** Phone — *Needs ordering · 1* · *On the way · 2*. */
  needsOrderingCount: (n: string) => `Needs ordering · ${n}`,
  onTheWayCount: (n: string) => `On the way · ${n}`,
  /** *materials · estimated $4,838.60*. */
  estimatedTile: (amount: string) => `materials · estimated ${amount}`,
  now: 'now',
  head: {
    material: 'Material',
    qty: 'Qty',
    needed: COMMON.needed,
    comesFrom: 'Comes from',
    whereItStands: 'Where it stands',
    estCost: COMMON.estCost,
  },
  /** *needed tomorrow* — beside a group's title. */
  neededWhen: (when: string) => `needed ${when}`,
  /** Where it comes from — do-not-substitute 6. */
  source: {
    yard: 'From the yard',
    order_site: 'Order · delivered to the site',
    order_yard: 'Order · delivered to the yard',
    order_pickup: 'Order · crew picks up',
    field: COMMON.boughtInTheField,
  },
  /** The same inside a sentence — *Ordered on PO-1052 · delivered to the yard*, Board 18 C *crew picks up*. */
  sourceInLine: {
    order_site: 'delivered to the site',
    order_yard: 'delivered to the yard',
    order_pickup: 'crew picks up',
  },
  /** Where it stands — do-not-substitute 6, §8.2. */
  state: {
    to_order: 'To order',
    ordered: 'Ordered',
    ready_pickup: COMMON.readyForPickup,
    at_yard: 'At the yard',
    on_site: 'Delivered to site',
    loaded: 'Loaded',
    bought: 'Bought',
    used: COMMON.used,
  },
  swapped: 'Swapped',
  order: COMMON.order,
  swap: COMMON.swap,
  /** The detail under a line's state. */
  detail: {
    /** *Needed Fri by 7:00 · not ordered*. */
    notOrdered: (day: string, time: string) => `Needed ${day} by ${time} · not ordered`,
    /** *Arrives at the yard today, about 2 PM · PO-1052*. */
    arrives: (when: string, time: string, po: string) => `Arrives at the yard ${when}, about ${time} · ${po}`,
    /** *Marco picks up Fri 6:45 · PO-1051*. */
    picksUp: (name: string, when: string, po: string) => `${name} picks up ${when} · ${po}`,
    /** *Delivered Tue Oct 20 · PO-1043*. */
    delivered: (date: string, po: string) => `Delivered ${date} · ${po}`,
    /** *Delivered Tue Oct 20* — a delivery with no PO behind it. */
    deliveredOn: (date: string) => `Delivered ${date}`,
    /** *12 t used Wed · 6 t loaded this morning*. */
    usedLoaded: (used: string, day: string, loaded: string) => `${used} used ${day} · ${loaded} loaded this morning`,
    loadedThisMorning: 'Loaded this morning',
    held: 'Held for this visit',
    /** *Hilltop Hardware, Wed · receipt to review*. */
    bought: (vendor: string, day: string) => `${vendor}, ${day} · receipt to review`,
    /** *Used Wed*. */
    used: (day: string) => `Used ${day}`,
    /** Board 18 C — *Noted by Jen Hollis, Thu 9:44 AM · PO-1051*. */
    noted: (name: string, when: string, po: string) => `Noted by ${name}, ${when} · ${po}`,
  },
  /** Under a line's name — *modified stone · 12 t used*. */
  partUsed: (detail: string, qty: string) => `${detail} · ${qty} used`,
  /** Board 18 C — *gas-ready · 1 · Day 3 · crew picks up*. */
  lineSub: (detail: string, qty: string, n: string, source: string) => `${detail} · ${qty} · Day ${n} · ${source}`,
  /** Phone — *1 · Order · crew picks up*. */
  qtySource: COMMON.dot,
  /** *$12.75 → $14.20 each*. */
  costChange: (was: string, now: string, unit: string) => `${was} → ${now} ${unit}`,
  /** *$689.00 / each*. */
  perUnit: COMMON.per,
  /** *each* — a line sold by the each (*$689.00 / each*, *$12.75 → $14.20 each*). */
  each: 'each',
  /** A line opened — Board 2. */
  story: {
    title: 'This line’s story',
    /** *Planned from Q-2184*. */
    planned: (quote: string) => `Planned from ${quote}`,
    /** *Capital Hardscape Supply: backordered to Nov 9*. */
    backordered: (vendor: string, until: string) => `${vendor}: backordered to ${until}`,
    /** *logged by Kyle Brandt (purchasing)*. */
    loggedBy: (name: string) => `logged by ${name} (purchasing)`,
    /** *Swapped to Belgard Bullnose, Pewter · $14.20*. */
    swapped: (to: string, price: string) => `Swapped to ${to} · ${price}`,
    /** *Jen Hollis · reason: backordered · fixed price, so the visit pays +$34.80 · Karen OK’d the colour by text*. */
    swappedLine: (name: string, reason: string, delta: string, note: string) =>
      `${name} · reason: ${reason} · fixed price, so the visit pays ${delta} · ${note}`,
    /** *Ordered on PO-1052 · delivered to the yard* — `to` from `ORDER.toInLine`. */
    ordered: (po: string, to: string) => `Ordered on ${po} · ${to}`,
    /** *Kyle Brandt · Capital Hardscape Supply confirms Thu, about 2 PM*. */
    confirms: (name: string, vendor: string, day: string, time: string) => `${name} · ${vendor} confirms ${day}, about ${time}`,
    /** *Arrives at the yard · then loads for Day 3*. */
    arrivesThenLoads: (n: string) => `Arrives at the yard · then loads for Day ${n}`,
    /** *the receiver ticks it in; the crew sees it on Friday’s load list*. */
    receiverTicks: (weekday: string) => `the receiver ticks it in; the crew sees it on ${weekday}’s load list`,
  },
  /** **What it costs the visit** — Board 2, internal. */
  costs: {
    title: 'What it costs the visit',
    internal: COMMON.internal,
    fixedPrice: COMMON.fixedPrice,
    /** *Planned 24 × $12.75 = $306.00*. */
    planned: (qty: string, each: string, total: string) => `Planned ${qty} × ${each} = ${total}`,
    /** *Now 24 × $14.20 = $340.80*. */
    now: (qty: string, each: string, total: string) => `Now ${qty} × ${each} = ${total}`,
    /** *· the visit pays +$34.80*. */
    visitPays: (delta: string) => `· the visit pays ${delta}`,
    /** *Karen’s price does not change. A change order is Tom Reyes’s to start — none was asked for.* */
    priceStays: (customer: string, salesperson: string) =>
      `${customer}’s price does not change. A change order is ${salesperson}’s to start — none was asked for.`,
    openSwapNote: 'Open the swap note',
  },
  /** *10 materials*. */
  footer: (n: string) => `${n} materials`,
  footerSub: 'the crew sees these on the iPad — the same lines, no costs',
  /** By day — Board 3 A, §8.4. */
  byDay: {
    used: COMMON.used,
    /** *from Marco’s close-out, 5:06 PM*. */
    fromCloseOut: (name: string, time: string) => `from ${name}’s close-out, ${time}`,
    /** *6 t left for Day 2*. */
    leftFor: (qty: string, n: string) => `${qty} left for Day ${n}`,
    bought: COMMON.boughtInTheField,
    boughtSub: 'by the day it was bought',
    /** *for Day 2 · Hilltop Hardware · Sam Okoro · receipt to review*. */
    boughtFor: (n: string, vendor: string, name: string) => `for Day ${n} · ${vendor} · ${name} · receipt to review`,
    loaded: 'Loaded at the yard',
    onSite: 'Already on site',
    /** *delivered Tue*. */
    delivered: (day: string) => `delivered ${day}`,
    /** *2 not ready*. */
    notReady: (n: string) => `${n} not ready`,
    load: 'Load at the yard',
    held: 'held for this visit',
    /** *arriving today, about 2 PM · PO-1052*. */
    arriving: (when: string, time: string, po: string) => `arriving ${when}, about ${time} · ${po}`,
    pickUp: 'Pick up on the way',
    /** *not ordered yet — needed by 7:00*. */
    notOrderedYet: (time: string) => `not ordered yet — needed by ${time}`,
    /** *the rest of the 420 sq ft*. */
    restOf: (qty: string) => `the rest of the ${qty}`,
  },
  /** Board 18 C — a line's ⋯ while purchasing is off (§27.3). */
  notedMenu: {
    readyForPickup: 'Mark ready for pickup',
    /** *Capital says it’s ready*. */
    readyForPickupSub: (vendor: string) => `${vendor} says it’s ready`,
    deliveredSite: 'Mark delivered to the site',
    deliveredYard: 'Mark delivered to the yard',
    open: COMMON.open,
    /** *noted · their order 55102*. */
    openSub: (vendorRef: string) => `noted · their order ${vendorRef}`,
    takeOff: 'Take it off the order',
    /** *tell Capital yourself*. */
    takeOffSub: (vendor: string) => `tell ${vendor} yourself`,
  },
  /** §8.3 — a line's ⋯ (contractText, §8.3); its sheets reuse the menu's words as their titles and buttons. */
  menu: {
    edit: COMMON.edit,
    spread: 'Spread over days',
    swap: COMMON.swap,
    move: 'Move to another visit',
    remove: 'Remove',
  },
  /** The parts of the line's story the board draws joined, for a story missing one of them. */
  storyParts: {
    /** *Swapped to Belgard Bullnose, Pewter* — without the price, for a role without *Costs & margins*. */
    swappedTo: (to: string) => `Swapped to ${to}`,
    /** *Jen Hollis · reason: backordered*. */
    swappedWho: (name: string, reason: string) => `${name} · reason: ${reason}`,
    /** *fixed price, so the visit pays +$34.80*. */
    swappedPays: (delta: string) => `fixed price, so the visit pays ${delta}`,
  },
} as const

// ===========================================================================
// Swap — Board 4, §9
// ===========================================================================

export const SWAP = {
  /** *Swap — Wall caps*. */
  title: (name: string) => `Swap — ${name}`,
  /** *V-1187 · not bought yet, so it’s an easy swap*. */
  subFixed: (reference: string) => `${reference} · not bought yet, so it’s an easy swap`,
  subTimeAndMaterials: 'a time & materials visit · not bought yet, so it’s an easy swap',
  subOrdered: 'ordered, so purchasing changes it',
  /** *24 · Day 3 · $12.75 each · from Q-2184*. */
  fromLine: (qty: string, n: string, each: string, quote: string) => `${qty} · Day ${n} · ${each} each · from ${quote}`,
  /** Board 4 C — *24 · Day 3*. */
  qtyDay: (qty: string, n: string) => `${qty} · Day ${n}`,
  why: COMMON.why,
  /** §4.3 `swap_reason`. */
  reasons: {
    backordered: 'Backordered',
    out_of_stock: 'Out of stock',
    customer_asked: 'Customer asked',
    better_fit: 'Better fit',
    other: 'Other',
  },
  /** The reason inside a sentence (Off the plan, the line's story) — *backordered* is drawn; the rest are §9.2's (contractText). */
  reasonInSentence: {
    backordered: 'backordered',
    out_of_stock: 'out of stock',
    customer_asked: 'customer asked',
    better_fit: 'a better fit',
  },
  swapTo: 'Swap to',
  bestMatch: 'Best match',
  /** *same size · matches the Pewter pavers* — what it matches, as the server names it. */
  matches: (what: string) => `same size · matches the ${what}`,
  vendorHas: COMMON.vendorHas,
  inStock: 'In stock',
  /** *a shade lighter than the pavers*. */
  lighterThan: (noun: string) => `a shade lighter than the ${noun}`,
  /** *30 at the yard now*. */
  atYardNow: (n: string) => `${n} at the yard now`,
  /** *+$1.45 each*. */
  each: (delta: string) => `${delta} each`,
  findAnother: '＋ Find another item · or split it (some now, the rest later)',
  internal: COMMON.internal,
  fixedPrice: COMMON.fixedPrice,
  timeAndMaterials: COMMON.timeAndMaterials,
  /** *visit pays +$34.80*. */
  visitPays: (delta: string) => `visit pays ${delta}`,
  /** *Karen’s price doesn’t change. 24 × $14.20 = $340.80 against $306.00 planned — the visit’s margin carries it.* */
  fixedNote: (customer: string, qty: string, each: string, total: string, planned: string) =>
    `${customer}’s price doesn’t change. ${qty} × ${each} = ${total} against ${planned} planned — the visit’s margin carries it.`,
  /** *Ask Tom Reyes for a change order*. */
  askChangeOrder: (name: string) => `Ask ${name} for a change order`,
  /** *A change order is sales’ to start. This sends Tom a task with the swap attached; the price only changes if Karen approves one.* */
  askChangeOrderSub: (salesperson: string, customer: string) =>
    `A change order is sales’ to start. This sends ${salesperson} a task with the swap attached; the price only changes if ${customer} approves one.`,
  tellTheCrew: 'Tell the crew',
  tellTheCrewSub: '— adds a note on For the crew',
  billsAtNewPrice: 'bills at the new price',
  /** *The customer is billed for what is used, at the new item’s price: 24 × $19.90 = $477.60 (was $428.40). Cost +$34.80, price +$49.20 — the margin holds.* */
  tmNote: (qty: string, each: string, total: string, was: string, cost: string, price: string) =>
    `The customer is billed for what is used, at the new item’s price: ${qty} × ${each} = ${total} (was ${was}). Cost ${cost}, price ${price} — the margin holds.`,
  tellTheCustomer: 'Tell the customer',
  tellTheCustomerSub: 'A text to the contact with the new item and price. No change order on time & materials — the invoice follows what is used.',
  cancel: COMMON.cancel,
  swap: COMMON.swap,
  /** Board 4 C — a line on a placed order. */
  blocked: {
    title: 'It’s on a placed order.',
    /** *PO-1052 went to Capital Hardscape Supply Tue at 11:20 AM. To change it, the vendor has to take the line back first.* */
    line: (po: string, vendor: string, day: string, time: string) =>
      `${po} went to ${vendor} ${day} at ${time}. To change it, the vendor has to take the line back first.`,
    open: COMMON.open,
    /** *Placed · Capital Hardscape Supply · delivers today*. */
    openSub: (state: string, vendor: string, when: string) => `${state} · ${vendor} · delivers ${when}`,
    /** *Ask Kyle Brandt to cancel the line*. */
    askCancel: (name: string) => `Ask ${name} to cancel the line`,
    askCancelSub: 'a task for purchasing with this note — then Swap comes back',
    bought: 'Already delivered or used? Then it’s bought: return it on the PO, or use it and add the new item as its own line.',
    close: COMMON.close,
  },
} as const

// ===========================================================================
// Order — Board 3 B, C; Board 18 B (§8.4, §27.3)
// ===========================================================================

export const ORDER = {
  /** *Order — Fire pit kit, Belgard Weston 46 in*. */
  title: (name: string) => `Order — ${name}`,
  /** *for V-1187 · Day 3 · needed Fri Oct 23 by 7:00*. */
  sub: (reference: string, n: string, date: string, time: string) => `for ${reference} · Day ${n} · needed ${date} by ${time}`,
  /** *Marco is already stopping at Capital Hardscape Supply*. */
  stopping: (name: string, vendor: string) => `${name} is already stopping at ${vendor}`,
  /** *Fri at 6:45 for PO-1051. Add the kit to that order and it comes back on the same trip.* */
  stoppingSub: (day: string, time: string, po: string, noun: string) =>
    `${day} at ${time} for ${po}. Add the ${noun} to that order and it comes back on the same trip.`,
  addTo: COMMON.addTo,
  /** *Capital Hardscape Supply · Marco Diaz picks up Fri 6:45 · the vendor gets the updated order*. */
  addToSub: (vendor: string, name: string, when: string) => `${vendor} · ${name} picks up ${when} · the vendor gets the updated order`,
  /** Board 18 B — *… · noted Tue by Kyle Brandt*. */
  addToSubNoted: (vendor: string, name: string, when: string, day: string, by: string) =>
    `${vendor} · ${name} picks up ${when} · noted ${day} by ${by}`,
  newOrder: 'A new order',
  newOrderSub: 'pick the vendor, how it gets there and when',
  vendor: COMMON.vendor,
  /** *2200 Paxton St, Harrisburg · 6 min from the yard*. */
  fromYard: (address: string, minutes: string) => `${address} · ${minutes} min from the yard`,
  /** *Simpson Ferry Rd, Mechanicsburg · 19 min* — the vendors after the first. */
  fromYardShort: (address: string, minutes: string) => `${address} · ${minutes} min`,
  /** *3 in stock*. */
  inStock: (n: string) => `${n} in stock`,
  quantity: 'Quantity',
  needed: COMMON.needed,
  /** *Fri Oct 23 · by 7:00 AM*. */
  neededBy: (date: string, time: string) => `${date} · by ${time}`,
  howItGetsThere: COMMON.howItGetsThere,
  /** §8.4's three ways. */
  how: {
    pickup: COMMON.crewPicksUp,
    site: 'Delivered to the site',
    yard: 'Delivered to the yard',
  },
  /** The same inside a sentence — *Ordered on PO-1052 · delivered to the yard*. */
  toInLine: {
    site: MATERIALS.sourceInLine.order_site,
    yard: MATERIALS.sourceInLine.order_yard,
    pickup: MATERIALS.sourceInLine.order_pickup,
  },
  picksUp: COMMON.picksUp,
  when: COMMON.when,
  internal: COMMON.internal,
  /** *Est. $689.00 · this price $689.00*. */
  priceLine: (estimate: string, price: string) => `Est. ${estimate} · this price ${price}`,
  noDifference: 'no difference',
  /** *Kyle Brandt (purchasing) is told. The kit joins Friday’s pickup on Marco’s iPad.* */
  footer: (purchaser: string, noun: string, weekday: string, picker: string) =>
    `${purchaser} (purchasing) is told. The ${noun} joins ${weekday}’s pickup on ${picker}’s iPad.`,
  cancel: COMMON.cancel,
  /** §8.4 — the primary when *A new order* is chosen (contractText). */
  placeOrder: 'Place order',
  /** Board 3 C — the toast: *Added to PO-1051 — Capital Hardscape Supply has the updated order*. */
  added: (po: string, vendor: string) => `Added to ${po} — ${vendor} has the updated order`,
  undo: COMMON.undo,
  /** Board 3 C — the line after: *PO-1051 · added 9:44 AM by Jen Hollis*. */
  addedLine: (po: string, time: string, name: string) => `${po} · added ${time} by ${name}`,
  /** Board 18 B — purchasing not switched on (§27.3). */
  noted: {
    bandTitle: 'Rowted won’t send this to the vendor yet',
    /** *— purchasing isn’t switched on. Call or email Capital Hardscape Supply, then note it here. …* */
    band: (vendor: string) =>
      `— purchasing isn’t switched on. Call or email ${vendor}, then note it here. It is kept as a real order, so it is already in purchasing the day purchasing is switched on.`,
    tag: COMMON.noted,
    whoYouSpokeTo: 'Who you spoke to',
    /** The faint word at the right of the two fields' labels. */
    optional: 'optional',
    theirOrderNumber: 'Their order number',
    /** *Est. $689.00 · price they gave*. */
    priceLine: (estimate: string) => `Est. ${estimate} · price they gave`,
    /** *Kept on PO-1051 as noted by Jen Hollis. The kit joins Friday’s pickup on Marco’s iPad.* */
    footer: (po: string, name: string, noun: string, weekday: string, picker: string) =>
      `Kept on ${po} as noted by ${name}. The ${noun} joins ${weekday}’s pickup on ${picker}’s iPad.`,
    /** *Note it on PO-1051*. */
    noteItOn: (po: string) => `Note it on ${po}`,
    /** §27.7 — a new noted order. */
    noteTheOrder: 'Note the order',
  },
} as const

// ===========================================================================
// Equipment — Board 5 A, Board 18 G, §10.1, §27.5
// ===========================================================================

export const EQUIPMENT = {
  title: COMMON.companyEquipment,
  /** *planned by type from Q-2184 · a unit is picked for each day*. */
  sub: (quote: string) => `planned by type from ${quote} · a unit is picked for each day`,
  add: 'Add equipment',
  head: {
    type: COMMON.type,
    /** *Day 1 · Wed Oct 21* · *Day 2 · Thu Oct 22 · today*. */
    day: COMMON.dayDate,
    dayRelative: COMMON.dayDateRelative,
    hours: COMMON.hours,
    estCost: COMMON.estCost,
  },
  byType: 'company · by type',
  cell: {
    /** *8.0 h used*. */
    used: COMMON.hoursUsed,
    /** *on site · 6 h planned*. */
    onSite: (hours: string) => `on site · ${hours} h planned`,
    /** *stays on site · 4 h planned*. */
    staysOnSite: (hours: string) => `stays on site · ${hours} h planned`,
    /** *hauled base · 4 h planned*. */
    hauled: (noun: string, hours: string) => `hauled ${noun} · ${hours} h planned`,
    /** *2 h planned*. */
    planned: COMMON.hoursPlanned,
    notNeeded: '— not needed',
    /** *Any wet saw*. */
    any: (type: string) => `Any ${type}`,
    /** *picked at 6:30 · 2 free*. */
    picked: (time: string, free: string) => `picked at ${time} · ${free} free`,
    /** Board 18 G — *no units yet · 8 h*. */
    noUnits: (hours: string) => `no units yet · ${hours} h`,
  },
  /** *18 h planned* · *8.0 h used* · *none used yet*. */
  hoursPlanned: COMMON.hoursPlanned,
  hoursUsed: COMMON.hoursUsed,
  noneUsed: 'none used yet',
  /** Board 18 G. */
  addUnit: 'Add a unit',
  /** *4 types* (*1 type*). */
  footer: (n: number) => (n === 1 ? `${String(n)} type` : `${String(n)} types`),
  /** *hours used post at each unit’s rate when the crew closes out the day · $352.00 so far*. */
  footerSub: (soFar: string) => `hours used post at each unit’s rate when the crew closes out the day · ${soFar} so far`,
  /** The same, without the money — a role without *Costs & margins* (§2.3). */
  footerSubHours: 'hours used post at each unit’s rate when the crew closes out the day',
  /** *56 h · 20 used*. */
  footerHours: (planned: string, used: string) => `${planned} h · ${used} used`,
  /** Board 18 G. */
  noUnitsFooter: 'Hours used are entered when the day is closed out and post at the quote’s rate until the unit has its own.',
  /** *$32 / h* — a type's rate under its estimate. */
  perHour: (amount: string) => COMMON.per(amount, COMMON.hourUnit),
  /** **Add equipment**'s popup (§7.9): *Type*, the days, and *Hours per day* (`contractText`). */
  hoursPerDay: 'Hours per day',
} as const

/** A day's unit — Board 5 B, §10.1. */
export const UNIT_PICKER = {
  /** *Wet saw · Fri Oct 23*. */
  title: COMMON.dot,
  decide: 'Decide in the morning',
  /** *the yard picks one at 6:30 — 2 free tonight*. */
  decideSub: (time: string, free: string) => `the yard picks one at ${time} — ${free} free tonight`,
  thisVisitToday: 'on this visit today · keep it on site',
  /** *at the yard · free Friday*. */
  atYardFree: (weekday: string) => `at the yard · free ${weekday}`,
  /** *in the shop — blade guard*. */
  inShop: (why: string) => `in the shop — ${why}`,
  footer: 'A unit picked here is held for the day; one already on another visit that day isn’t offered.',
} as const

/** **Add a unit** — Board 18 H, §27.5. */
export const ADD_UNIT = {
  title: EQUIPMENT.addUnit,
  /** *a mini skid steer for V-1187*. */
  sub: (type: string, reference: string) => `a ${type} for ${reference}`,
  type: COMMON.type,
  unitNumber: 'Unit number',
  livesAt: 'Lives at',
  costPerHour: 'Cost per hour',
  costPerHourHelp: 'internal · used for the visit’s cost',
  /** *$ 32.00 / h* — the field's sign and unit. */
  dollar: '$',
  perHour: '/ h',
  band: 'It goes into Equipment’s own list, so the module starts with it — nothing to enter twice.',
  cancel: COMMON.cancel,
  /** *Add MSS-03*. */
  add: (tag: string) => `Add ${tag}`,
} as const

// ===========================================================================
// Rented from a vendor — Board 5 A, Board 6 A, Board 18 I, §10.2, §27.4
// ===========================================================================

export const RENTALS = {
  title: 'Rented from a vendor',
  sub: 'where to get it, who picks it up, when it goes back',
  add: 'Rent from a vendor',
  /** The rental's chip — §10.2; *Picked up* and *Cancelled* are the contract's words (`contractText`). */
  state: {
    to_book: 'To book',
    booked: 'Booked',
    picked_up: 'Picked up',
    returned: 'Returned',
    cancelled: HEADER.status.cancelled,
  },
  /** *4710 Industrial Rd, Harrisburg* — the vendor block's street and town. */
  address: (line1: string, city: string) => `${line1}, ${city}`,
  navigate: COMMON.navigate,
  /** The timeline. */
  stops: {
    /** *Booked · RA-88213*. */
    booked: (confirmation: string) => `Booked · ${confirmation}`,
    /** *Picked up · Jesse Lutz*. */
    pickedUp: (name: string) => `Picked up · ${name}`,
    /** *Returned · Jesse Lutz*. */
    returned: (name: string) => `Returned · ${name}`,
    /** *Pick up · Jesse Lutz*. */
    pickUp: (name: string) => `Pick up · ${name}`,
    /** *Return by · Jesse Lutz*. */
    returnBy: (name: string) => `Return by · ${name}`,
  },
  /** *$345.00 a day × 1*. */
  rate: (rate: string, days: string) => `${rate} a day × ${days}`,
  /** Board 6 A — *estimated $95.00 a day × 1*. */
  rateEstimated: (rate: string, days: string) => `estimated ${rate} a day × ${days}`,
  internal: COMMON.internal,
  /** *Keystone’s bill: not in yet — purchasing matches it when it comes*. */
  billNotIn: (vendor: string) => `${vendor}’s bill: not in yet — purchasing matches it when it comes`,
  /**
   * *Note for the crew: hitch is a 2 in ball — take the F-250* — the note as
   * typed (*Hitch is a 2 in ball — take the F-250.*, Board 6 B) set after the
   * colon, as Board 5 A and 18 I print it.
   */
  note: (note: string) => `Note for the crew: ${afterColon(note)}`,
  change: 'Change',
  cancelBooking: 'Cancel booking',
  /** Board 6 A — planned, not booked. */
  toBook: {
    /** *Fri Oct 23 · from Q-2184*. */
    when: COMMON.fromQuote,
    /** *Planned on the quote, not booked with a vendor yet. Friday’s prep counts it as not ready until it is.* */
    line: (weekday: string) =>
      `Planned on the quote, not booked with a vendor yet. ${weekday}’s prep counts it as not ready until it is.`,
    bookIt: OVERVIEW.ready.bookIt,
  },
  /** Board 18 I — the crew app has no rental stops yet (§27.4). */
  standIn: {
    markPickedUp: 'Mark picked up',
    /** §27.7. */
    markReturned: 'Mark returned',
    line: 'The crew app doesn’t show rental stops yet, so the office marks the pickup and the return here.',
  },
} as const

/** **Rent from a vendor** — Board 6 B, §10.2. */
export const RENT = {
  title: RENTALS.add,
  /** *V-1187 · planned on Q-2184 · Tue Oct 20, 2:10 PM*. */
  sub: (reference: string, quote: string, when: string) => `${reference} · planned on ${quote} · ${when}`,
  what: 'What',
  type: COMMON.type,
  vendor: COMMON.vendor,
  rentedHere: 'rented here',
  /** *4 times this year*. */
  timesThisYear: (n: string) => `${n} times this year`,
  /** *9 min from the yard · 18 min on to the site*. */
  drive: (fromYard: string, onToSite: string) => `${fromYard} min from the yard · ${onToSite} min on to the site`,
  days: COMMON.days,
  /** *Day 1 · Wed*. */
  day: COMMON.dayDate,
  howItGetsThere: COMMON.howItGetsThere,
  how: {
    pickup: COMMON.crewPicksUp,
    delivery: 'Vendor delivers to the site',
  },
  picksUp: COMMON.picksUp,
  at: 'At',
  /** *at the site about 7:30*. */
  atSite: (time: string) => `at the site about ${time}`,
  takesItBack: 'Takes it back',
  by: 'By',
  /** *Keystone closes at 5:00*. */
  closesAt: (vendor: string, time: string) => `${vendor} closes at ${time}`,
  /** §10.2 (`contractText`) — added to the amber hint when *By* is after they close. */
  afterClose: 'after they close',
  rate: 'Rate',
  /** *$95.00* — the rate field's sign. */
  dollar: '$',
  /** *a day · est. $95.00*. */
  rateSub: (estimate: string) => `a day · est. ${estimate}`,
  confirmation: 'Confirmation',
  /** The faint word at the right of *Type*, *Confirmation* and *Note for the crew* — Order's. */
  optional: ORDER.noted.optional,
  note: COMMON.noteForTheCrew,
  /** *The pickup and the return go on Jesse’s Friday in the crew app.* */
  footer: (name: string, weekday: string) => `The pickup and the return go on ${name}’s ${weekday} in the crew app.`,
  cancel: COMMON.cancel,
  book: 'Book',
} as const

// ===========================================================================
// Time — Board 7, §11
// ===========================================================================

export const TIME = {
  allDays: 'All days',
  /** *Day 1 · Wed*. */
  dayChip: COMMON.dayDate,
  legend: {
    onThisVisit: COMMON.onThisVisit,
    yardAndDrive: 'yard & drive — not on this visit',
    lunch: COMMON.lunch,
    planned: COMMON.planned,
  },
  addTime: 'Add time',
  onThisVisit: 'On this visit',
  /** *of 76.0 h estimated · projected 77.0 h*. */
  hoursSub: (estimated: string, projected: string) => `of ${estimated} h estimated · projected ${projected} h`,
  laborCost: 'Labor cost',
  internal: COMMON.internal,
  soFar: COMMON.soFar,
  /** *estimated $2,584.00 (76 h × $34.00) · projected $2,542.75*. */
  laborSub: (estimated: string, hours: string, rate: string, projected: string) =>
    `estimated ${estimated} (${hours} h × ${rate}) · projected ${projected}`,
  toLookAt: 'To look at',
  flags: 'flags',
  /** *1 punch edited, with a reason*. */
  edited: (n: string) => `${n} punch edited, with a reason`,
  /** *Thu Oct 22 · today*. */
  dayDate: COMMON.dateRelative,
  /** *On the clock · 3*. */
  onTheClockCount: (n: string) => `On the clock · ${n}`,
  /** *so far of 27 planned*. */
  soFarOfPlanned: (planned: string) => `so far of ${planned} planned`,
  /** *of 27 planned*. */
  ofPlanned: COMMON.ofPlanned,
  role: { lead: 'Crew lead', crew: COMMON.crew },
  /** The punches in words. */
  punch: {
    /** *in · yard* · *in · Keystone*. */
    in: (place: string) => `in · ${place}`,
    /** *in · Keystone, returned the excavator*. */
    inReturned: (place: string, noun: string) => `in · ${place}, returned the ${noun}`,
    arrived: 'arrived',
    onSiteNow: 'on site now',
    lunch: COMMON.lunch,
    /** *lunch · end added by Marco*. */
    lunchEndAddedBy: (name: string) => `lunch · end added by ${name}`,
    left: 'left',
    /** *out · yard*. */
    out: (place: string) => `out · ${place}`,
    yard: COMMON.yard,
  },
  onThisVisitHours: COMMON.onThisVisit,
  onTheClock: COMMON.onTheClock,
  /** *22 h planned · Marco, Sam and Jesse · 7:00 start · nothing punched yet*. */
  notStarted: (hours: string, names: readonly string[], start: string) =>
    names.length > 1
      ? `${hours} h planned · ${allButLast(names)} and ${lastOf(names)} · ${start} start · nothing punched yet`
      : `${hours} h planned · ${lastOf(names)} · ${start} start · nothing punched yet`,
  /** §11 (contractText): **Look at it** on a flag's amber line. */
  lookAtIt: 'Look at it',
  /** §11 (contractText): a flag's amber line on a person's row. */
  flag: {
    open: 'open at the end of the day',
    /** *clocked in 2.1 mi from the site*. */
    clockedIn: (distance: string) => `clocked in ${distance} from the site`,
    clockedOut: (distance: string) => `clocked out ${distance} from the site`,
    /** *overlaps V-1183*. */
    overlaps: (visit: string) => `overlaps ${visit}`,
    /** *2.1 mi* — Board 10 B's distance. */
    distance: (miles: string) => `${miles} mi`,
  },
} as const

/** **Edit a punch** — Board 7 B, §11. */
export const EDIT_PUNCH = {
  /** *Edit a punch — Jesse Lutz*. */
  title: (name: string) => `Edit a punch — ${name}`,
  /** *Wed Oct 21 · Day 1 · V-1187*. */
  sub: (date: string, n: string, reference: string) => `${date} · Day ${n} · ${reference}`,
  punch: 'Punch',
  /** The punch — only what a board draws. */
  kind: { lunch_end: 'End lunch' },
  time: TABS.time,
  reason: 'Reason',
  /** *Required for any change. Jesse is told, and it shows on this punch for good.* */
  reasonHelp: (name: string) => `Required for any change. ${name} is told, and it shows on this punch for good.`,
  thisPunch: 'This punch',
  /** *added it on the crew iPad · Wed 4:58 PM — “forgot to tap back in”*. */
  addedOnIpad: (when: string, note: string) => `added it on the crew iPad · ${when} — “${note}”`,
  toldNoDispute: 'was told and didn’t dispute it',
  original: 'The original stays in History.',
  cancel: COMMON.cancel,
  save: COMMON.save,
} as const

/** **Add time for the crew** — Board 18 E, §27.4 (Board 7's *Add time* opens it in both modes). */
export const ADD_TIME = {
  title: 'Add time for the crew',
  /** *V-1187 · Patio, seat wall and fire pit — install*. */
  sub: COMMON.dot,
  person: 'Person',
  day: COMMON.day,
  /** *Day 1 · Wed Oct 21*. */
  dayValue: COMMON.dayDate,
  clockedIn: 'Clocked in',
  /** *at Keystone Equipment Rental* · *at Harrisburg yard*. */
  at: (place: string) => `at ${place}`,
  arrived: 'Arrived',
  left: 'Left',
  clockedOut: 'Clocked out',
  lunchFrom: 'Lunch from',
  lunchTo: 'Lunch to',
  /** *9.0 h* **on this visit** · *10:15* **on the clock** — runs beside the bold figures. */
  onThisVisit: COMMON.onThisVisit,
  onTheClock: COMMON.onTheClock,
  internal: COMMON.internal,
  why: COMMON.why,
  /** *Kept as Jesse Lutz’s punches, entered by Jen Hollis, with the reason.* */
  footer: (person: string, by: string) => `Kept as ${person}’s punches, entered by ${by}, with the reason.`,
  cancel: COMMON.cancel,
  add: 'Add the time',
} as const

// ===========================================================================
// Money — Board 8, §12
// ===========================================================================

export const MONEY = {
  kpi: {
    price: 'Price',
    /** *fixed · from Q-2184, approved Oct 9*. */
    priceSub: (quote: string, date: string) => `fixed · from ${quote}, approved ${date}`,
    estimatedCost: 'Estimated cost',
    estimatedCostSub: 'from the quote, locked at approval',
    soFar: COMMON.soFarLabel,
    soFarSub: 'as worked, used and bought',
    projected: COMMON.projectedLabel,
    projectedSub: 'so far + the rest of the plan',
    margin: COMMON.marginLabel,
    /** *projected · estimated 37.8%*. */
    marginSub: (estimated: string) => `projected · estimated ${estimated}`,
    internal: COMMON.internal,
  },
  byCategory: {
    title: 'By category',
    /** *estimate from Q-2184 · so far as it happens · projected = so far + the rest of the plan*. */
    sub: (quote: string) => `estimate from ${quote} · so far as it happens · projected = so far + the rest of the plan`,
    /** *Open J-3271 Money*. */
    openJob: (job: string) => `Open ${job} Money`,
    head: {
      category: COMMON.category,
      estimate: COMMON.estimateLabel,
      soFar: COMMON.soFarLabel,
      projected: COMMON.projectedLabel,
      vsEstimate: COMMON.vsEstimate,
      ofProjected: 'so far, of projected',
    },
  },
  /** §6's categories, in order — *Subcontractors* is drawn on no board. */
  categories: {
    labor: COMMON.labor,
    materials: TABS.materials,
    equipment: EQUIPMENT.title,
    rental: RENTALS.title,
    fees: COMMON.fees,
    /** §6.1 (contractText) — drawn only when there are any. */
    subs: 'Subcontractors',
  },
  sub: {
    /** *76.0 h estimated · 35.9 h so far · 77.0 h projected*. */
    labor: (estimated: string, soFar: string, projected: string) =>
      `${estimated} h estimated · ${soFar} h so far · ${projected} h projected`,
    /** *10 lines · used as the crew closes out, field buys as bought*. */
    materials: (n: string) => `${n} lines · used as the crew closes out, field buys as bought`,
    /** *56 h planned by type · 20 h used*. */
    equipment: (planned: string, used: string) => `${planned} h planned by type · ${used} h used`,
    /** *mini excavator Wed* — joined with `COMMON.dot`. */
    rental: (name: string, day: string) => `${name} ${day}`,
    /** *dump fee, 2 × $85.00 · Wed $91.40*. */
    fees: (name: string, count: string, each: string, day: string, spent: string) => `${name}, ${count} × ${each} · ${day} ${spent}`,
    /** The same line in its two parts, for a visit with more or fewer of either — *dump fee, 2 × $85.00* · *Wed $91.40*. */
    feesPlanned: (name: string, count: string, each: string) => `${name}, ${count} × ${each}`,
    feeSpent: (day: string, spent: string) => `${day} ${spent}`,
  },
  /** The lines that moved, under Materials. */
  moved: {
    /** *Wall caps — swapped to Belgard Bullnose, Pewter*. */
    swapped: (name: string, to: string) => `${name} — swapped to ${to}`,
    swappedFixed: 'fixed price: the visit pays the difference',
    /** *Construction adhesive — bought in the field*. */
    bought: (name: string) => `${name} — bought in the field`,
    /** *Hilltop Hardware receipt $64.52 · to review*. */
    receipt: (vendor: string, amount: string) => `${vendor} receipt ${amount} · to review`,
    /** *Hilltop Hardware receipt $64.52* — once it is no longer to review (the drawn line, without its state). */
    receiptOnly: (vendor: string, amount: string) => `${vendor} receipt ${amount}`,
    /** *8 more lines as planned*. */
    more: (n: string) => `${n} more lines as planned`,
  },
  totalCost: COMMON.totalCost,
  earns: COMMON.earns,
  /** *· price $14,250.00 less cost*. */
  earnsSub: (price: string) => `· price ${price} less cost`,
  margin: COMMON.marginLabel,
  /** §16.5 (contractText): the adjustments after close, under By category. */
  adjustments: 'Adjustments',
} as const

/** **Receipts** — Board 8, Board 16 D, §12. */
export const RECEIPTS = {
  title: 'Receipts',
  sub: 'bought in the field — on the visit the moment the crew sends them, flagged until someone looks',
  add: 'Add a receipt',
  /** Phone's green button. */
  addShort: 'Add receipt',
  /** A receipt's review — *Rejected* is drawn on no board (§4.7, contractText). */
  state: {
    to_review: 'To review',
    approved: 'Approved',
    rejected: 'Rejected',
  },
  amount: 'Amount',
  when: COMMON.when,
  sentBy: 'Sent by',
  for: 'For',
  paidWith: 'Paid with',
  /** How it was paid — *Account* is drawn on no board. */
  paid: {
    own_money: 'Own money',
    company_card: 'Company card',
    /** §4.7, contractText. */
    account: 'Account',
  },
  /** Inside a sentence — *paid with own money*, *· company card*. */
  paidWithInSentence: {
    own_money: 'own money',
    company_card: 'company card',
  },
  /** *Company card ··2208*. */
  card: (last4: string) => `Company card ··${last4}`,
  against: 'Against',
  /** *Construction adhesive · planned $45.00 · +$19.52*. */
  againstLine: (line: string, planned: string, delta: string) => `${line} · planned ${planned} · ${delta}`,
  /** Phone — *Construction adhesive · planned $45.00*. */
  againstLineShort: (line: string, planned: string) => `${line} · planned ${planned}`,
  /** *Pay Sam back*. */
  payBack: (name: string) => `Pay ${name} back`,
  payBackSub: 'on the next payroll',
  /** *On the visit since Wed 1:18 PM, flagged — approving clears the flag.* */
  flagged: (when: string) => `On the visit since ${when}, flagged — approving clears the flag.`,
  reject: 'Reject',
  approve: 'Approve',
  /** *Approved by Jen Hollis · Wed 6:12 PM*. */
  approvedBy: (name: string, when: string) => `Approved by ${name} · ${when}`,
  nothingToPayBack: 'Company card — nothing to pay back.',
  undoApproval: 'Undo approval',
  /** Phone — *receipt · V-1187*. */
  receiptOf: (reference: string) => `receipt · ${reference}`,
  /** *Wed Oct 21, 1:18 PM · Day 1*. */
  at: COMMON.atDay,
} as const

// ===========================================================================
// For the crew — Board 9, §13
// ===========================================================================

export const FOR_CREW = {
  /** The filter chips — *All · 9 · Notes · 4 · Files · 3 · Maps · 2*. */
  chips: {
    all: (n: string) => `All · ${n}`,
    notes: (n: string) => `Notes · ${n}`,
    files: (n: string) => `Files · ${n}`,
    maps: (n: string) => `Maps · ${n}`,
  },
  sub: 'rolled up from the contact, the property and the job, plus this visit’s own',
  notes: 'Notes',
  notesSub: 'newest first · pinned on top',
  /** Where a note lives — the source chip (§13.1). */
  source: {
    visit: COMMON.thisVisit,
    property: COMMON.property,
    contact: COMMON.contact,
    job: COMMON.job,
  },
  /** The note's author — *· Jen Hollis*. */
  by: COMMON.count,
  visitPinned: 'this visit only · pinned',
  /** A *This visit* note that is not pinned. */
  visitOnly: 'this visit only',
  edit: COMMON.edit,
  propertySub: 'access notes · every visit here',
  editOnProperty: 'Edit on the property',
  /** *on Karen at this property · every visit*. */
  contactSub: (name: string) => `on ${name} at this property · every visit`,
  editOnContact: 'Edit on the contact',
  /** *J-3271 · from Q-2184*. */
  jobTitle: COMMON.fromQuote,
  jobSub: 'the quote’s crew note, kept at approval',
  openJob: COMMON.open,
  /** *2 internal notes on J-3271 and Q-2184 stay in the office — the crew never sees them.* */
  internalStay: (n: string, on: readonly string[]) =>
    on.length > 1
      ? `${n} internal notes on ${allButLast(on)} and ${lastOf(on)} stay in the office — the crew never sees them.`
      : `${n} internal notes on ${lastOf(on)} stay in the office — the crew never sees them.`,
  files: 'Files',
  addFile: 'Add a file for the crew',
  pdf: 'PDF',
  /** *2 pages · Oct 9* · *1 page · Oct 14*. */
  pages: (n: string, date: string) => `${n} pages · ${date}`,
  page: (n: string, date: string) => `${n} page · ${date}`,
  maps: 'Maps',
  mapsSub: 'layers pinned to visits',
  /** *drawn by Tom Reyes · Oct 6*. */
  drawnBy: (name: string, date: string) => `drawn by ${name} · ${date}`,
} as const

/** **Add a note for the crew** — Board 9, §13.3 (and *Message crew*). */
export const CREW_NOTE = {
  title: COMMON.addANoteForTheCrew,
  sub: 'the crew reads it on the iPad before they start',
  whereItLives: 'Where it lives',
  /** §13.3's three homes. */
  home: {
    visit: COMMON.thisVisit,
    property: 'Every visit at this property',
    /** *Karen, everywhere*. */
    contact: (name: string) => `${name}, everywhere`,
  },
  tellNow: COMMON.tellTheCrewNow,
  add: 'Add note',
} as const

/** **What Marco sees** — the crew iPad's card, Board 9, §13.4. */
export const CREW_PREVIEW = {
  /** *What Marco sees* — the lead of today, else of the next day (§13.2). */
  title: (name: string) => `What ${name} sees`,
  when: 'the crew iPad, this morning',
  /** *Day 2 of 3 · Patio, seat wall and fire pit*. */
  sub: (n: string, count: string, name: string) => `Day ${n} of ${count} · ${name}`,
  navigate: COMMON.navigate,
  /** *Before you start · 4*. */
  beforeYouStart: (n: string) => `Before you start · ${n}`,
  /** *Plans · 3*. */
  plans: (n: string) => `Plans · ${n}`,
  /** *Map · 2*. */
  map: (n: string) => `Map · ${n}`,
  loadList: COMMON.loadList,
} as const

// ===========================================================================
// From the crew — Board 10, §14
// ===========================================================================

export const FROM_CREW = {
  /** *All · 15 · Photos · 11 · Notes · 2 · Receipts · 2*. */
  chips: {
    all: FOR_CREW.chips.all,
    photos: (n: string) => `Photos · ${n}`,
    notes: FOR_CREW.chips.notes,
    receipts: (n: string) => `Receipts · ${n}`,
  },
  allDays: TIME.allDays,
  day: COMMON.dayN,
  sub: 'Finished photos come on the last day — Review & close asks for one',
  upload: 'Upload',
  /** *Thu Oct 22 · today*. */
  dayDate: COMMON.dateRelative,
  /** *2 photos*. */
  photos: (n: string) => `${n} photos`,
  /** A crew photo's tag (§4.8). */
  tag: {
    before: 'Before',
    during: 'During',
    issue: 'Issue',
    finished: 'Finished',
  },
  closedOutTheDay: 'closed out the day',
  quoted: COMMON.quoted,
  used: MATERIALS.groups.used,
  equipmentHours: COMMON.equipmentHours,
  hours: COMMON.hours,
  /** *28.0 on the visit · 27 planned*. */
  hoursLine: (hours: string, planned: string) => `${hours} on the visit · ${planned} planned`,
  aReceipt: 'a receipt',
  /** *Dump fee — 2 loads of dirt and old concrete · company card*. */
  receiptLine: (description: string, paidWith: string) => `${description} · ${paidWith}`,
  review: COMMON.review,
  aNoteAndAPhoto: 'a note and a photo',
  /** One photo, and a note on its own — the words of *a note and a photo*. */
  aPhoto: 'a photo',
  aNote: 'a note',
} as const

/** A photo, opened — Board 10 B, §14. */
export const LIGHTBOX = {
  /** *Wed Oct 21, 9:40 AM · Day 1*. */
  when: COMMON.atDay,
  /** *Taken at the site · 118 Lamp Post Ln*. */
  takenAt: (place: string) => `Taken at the site · ${place}`,
  /** *Taken 2.1 mi from the site* (amber, §14). */
  takenAway: (miles: string) => `Taken ${miles} mi from the site`,
  tags: 'Tags',
  /** *Karen sees it on the portal*. */
  share: (name: string) => `${name} sees it on the portal`,
  shareOff: 'off — crew photos are the office’s until someone shares them',
  download: 'Download',
  addToJob: 'Add to the job’s files',
} as const

// ===========================================================================
// Days — Board 11, Board 18 F, §15
// ===========================================================================

/** A day, opened beside the page — Board 11 A, §15.1. */
export const DAY_SHEET = {
  /** *Day 2 of 3 · Thu Oct 22*. */
  title: (n: string, count: string, date: string) => `Day ${n} of ${count} · ${date}`,
  /** *since 6:58 · 7.9 h so far of 27*. */
  sub: (time: string, hours: string, planned: string) => `since ${time} · ${hours} h so far of ${planned}`,
  crew: COMMON.crew,
  timeTab: 'Time tab',
  /** *lead · in 6:31 at the yard · arrived 6:58* — `place` is `COMMON.theYard` or a vendor. */
  leadLine: (time: string, place: string, arrived: string) => `lead · in ${time} at ${place} · arrived ${arrived}`,
  /** *in 6:44 at Keystone · arrived 7:12*. */
  personLine: (time: string, place: string, arrived: string) => `in ${time} at ${place} · arrived ${arrived}`,
  planned: COMMON.plannedLabel,
  /** *7:00 – 4:30 · 27 h · the second of three days* — `nth` and `count` from `COMMON.ordinalWords` / `numberWords`. */
  plannedLine: (from: string, to: string, hours: string, nth: string, count: string) =>
    `${from} – ${to} · ${hours} h · the ${nth} of ${count} days`,
  materialsToday: 'Materials today',
  materials: TABS.materials,
  onSite: COMMON.onSite,
  loaded: MATERIALS.state.loaded,
  equipmentToday: 'Equipment today',
  fromTheCrewToday: 'From the crew today',
  seeAll: COMMON.seeAll,
  closeOut: COMMON.closeOut,
  /** *Not yet. Marco closes out the day on the iPad — …* */
  notYet: (name: string) =>
    `Not yet. ${name} closes out the day on the iPad — what was used, equipment hours, a word on the day. It shows here, and on From the crew.`,
  /** Board 18 F — the crew app can't close out yet (§27.4). */
  notYetOffice: 'Not yet. The crew app can’t close out a day yet, so the office does it for the crew — what was used, equipment hours, a word on the day.',
  closeOutForCrew: 'Close out for the crew',
  move: 'Move this day',
  changeCrew: 'Change crew',
  skip: 'Skip this day',
  /** A day done — the board's two runs, *28.0 h* and *of 27 planned* (Board 1's tile). */
  hoursRun: COMMON.h,
  ofPlanned: COMMON.ofPlanned,
  /** *22 h planned* — a day to come. */
  subPlanned: COMMON.hoursPlanned,
  /** The plan line of a visit longer than the number words — the runs *7:00 – 4:30* and *27 h*. */
  range: COMMON.range,
  dot: COMMON.dot,
  toLoad: MATERIALS.byDay.load,
  pickUp: MATERIALS.byDay.pickUp,
  /** *Pavers 420 sq ft* · *Seat wall block 96*. */
  material: (name: string, qty: string) => `${name} ${qty}`,
  /** *Pavers 420 sq ft · Seat wall block 96*. */
  list: (items: readonly string[]) => items.join(' · '),
  /** A photo's tag — *During*. */
  tag: FROM_CREW.tag,
  /** *8:15 AM · Sand screeded, first rows laid.* */
  latest: COMMON.dot,
  /** A day's close-out, once it is in — the lead's word in quotes, then what From the crew prints. */
  quoted: COMMON.quoted,
  /** *closed out by Marco Diaz · Wed 5:06 PM*. */
  closedOutBy: (name: string, when: string) => `closed out by ${name} · ${when}`,
  /** *closed out by Jen Hollis for Marco Diaz · Wed 5:06 PM* (§27.4). */
  closedOutFor: (by: string, lead: string, when: string) => `closed out by ${by} for ${lead} · ${when}`,
  used: FROM_CREW.used,
  equipmentHours: COMMON.equipmentHours,
  hours: COMMON.hours,
  hoursLine: FROM_CREW.hoursLine,
  /** *MSS-03 8.0*. */
  unitHours: (tag: string, hours: string) => `${tag} ${hours}`,
  /** The footer's ⋯ (Board 16's word for the rest). */
  more: 'More',
  /** ⋯ — §15.1, only for a day not started (`contractText`). */
  remove: 'Remove this day',
  /** The sub of *Skip this day*, *Change crew* and *Remove this day* — *Day 2 · Thu Oct 22*. */
  daySub: COMMON.dayDate,
  why: COMMON.why,
  cancel: COMMON.cancel,
  /** *Skip this day* asks — §15.3's words (`contractText`). */
  keep: 'Its hours and materials stay on the visit for another day',
  drop: 'Its hours and materials are removed',
} as const

/** **Add a day** — Board 11 B, §15.2. */
export const ADD_DAY = {
  title: COMMON.addADay,
  /** *V-1187 · becomes Day 4 of 4, after Fri Oct 23*. */
  sub: (reference: string, n: string, count: string, after: string) => `${reference} · becomes Day ${n} of ${count}, after ${after}`,
  day: COMMON.day,
  off: 'off',
  /** *Hardscape 2 is free Mon and Tue · weather from the National Weather Service*. */
  free: (crew: string, days: readonly string[]) =>
    days.length > 1
      ? `${crew} is free ${allButLast(days)} and ${lastOf(days)} · weather from the National Weather Service`
      : `${crew} is free ${lastOf(days)} · weather from the National Weather Service`,
  crew: COMMON.crew,
  hours: COMMON.hours,
  whereFrom: 'Where the hours come from',
  /** *Split them off Day 3*. */
  split: (n: string) => `Split them off Day ${n}`,
  /** *Day 3 goes from 22 h to 14 h — the visit still plans 76 h*. */
  splitSub: (n: string, from: string, to: string, total: string) => `Day ${n} goes from ${from} h to ${to} h — the visit still plans ${total} h`,
  extra: 'They are extra',
  /** *the plan grows to 84 h, 8 h over the estimate · Off the plan says why*. */
  extraSub: (total: string, over: string) => `the plan grows to ${total} h, ${over} h over the estimate · Off the plan says why`,
  why: COMMON.why,
  /** The form kit's tag beside *Why*. */
  optional: ORDER.noted.optional,
  /** The unit after *Hours*. */
  unit: COMMON.hourUnit,
  cancel: COMMON.cancel,
  add: 'Add the day',
} as const

/** **Move Day 3** — Board 11 C, §15.3. */
export const MOVE_DAY = {
  /** *Move Day 3*. */
  title: (n: string) => `Move Day ${n}`,
  /** *V-1187 · and what depends on the date*. */
  sub: (reference: string) => `${reference} · and what depends on the date`,
  from: 'From',
  to: 'To',
  why: COMMON.why,
  /** *Monday works.* */
  works: (weekday: string) => `${weekday} works.`,
  /** *Hardscape 2 is free; sunny, 59°. Nothing else is booked out of the yard that morning.* */
  worksSub: (crew: string, forecast: string, temperature: string) =>
    `${crew} is free; ${forecast}, ${temperature}. Nothing else is booked out of the yard that morning.`,
  /** *What moves with it · 4*. */
  title2: (n: string) => `What moves with it · ${n}`,
  /** *Fri → Mon. Keystone has to agree: call (717) 555-0122, then mark it done.* */
  vendorAgree: (from: string, to: string, vendor: string, phone: string) =>
    `${from} → ${to}. ${vendor} has to agree: call ${phone}, then mark it done.`,
  callVendor: 'Call the vendor',
  /** *Pickup at Capital Hardscape Supply · PO-1051*. */
  pickupAt: (vendor: string, po: string) => `Pickup at ${vendor} · ${po}`,
  /** *Fri 6:45 → Mon 6:45 · the vendor gets the new date*. */
  poMoves: (from: string, to: string) => `${from} → ${to} · the vendor gets the new date`,
  movesItself: 'Moves itself',
  companyEquipment: EQUIPMENT.title,
  /** *MSS-03 and PC-07 stay on site over the weekend · TR-12 and a wet saw re-picked Monday*. */
  equipmentMoves: (stay: string, repicked: string, day: string) => `${stay} stay on site over the weekend · ${repicked} re-picked ${day}`,
  /** *a wet saw*. */
  aType: (type: string) => `a ${type}`,
  /** The two halves of `equipmentMoves`, for a move that has only one of them. */
  staysOnSite: (stay: string) => `${stay} stay on site over the weekend`,
  repicked: (repicked: string, day: string) => `${repicked} re-picked ${day}`,
  /** *Towable concrete mixer · Keystone · KR-4471*. */
  rentalTitle: COMMON.dot,
  /** *a text: “We’ve moved the last day to Monday Oct 26, as you asked.”* — the prepared text is the server's. */
  customerText: (text: string) => `a text: “${text}”`,
  editText: 'Edit the text',
  cancel: COMMON.cancel,
  move: 'Move the day',
} as const

// ===========================================================================
// Review & close — Board 12, §16
// ===========================================================================

export const CLOSE = {
  /** *Review & close — V-1187*. */
  title: (reference: string) => `Review & close — ${reference}`,
  /** *Fri Oct 23, 5:52 PM · after Day 3*. */
  after: (when: string, n: string) => `${when} · after Day ${n}`,
  /** *Thu Oct 22, 9:41 AM · Day 2 of 3*. */
  during: (when: string, n: string, count: string) => `${when} · Day ${n} of ${count}`,
  passes: 'Everything below passes — the visit can close.',
  /** *All 7 are still open — the crew is on Day 2 of 3.* */
  open: (n: string, day: string, count: string) => `All ${n} are still open — the crew is on Day ${day} of ${count}.`,
  /** §16.2's seven lines. */
  lines: {
    days: 'Every day closed out by the crew',
    time: TABS.time,
    receipts: RECEIPTS.title,
    rentals: RENTALS.title,
    leftovers: 'Leftovers back at the yard',
    equipment_hours: COMMON.equipmentHours,
    finished_photos: 'Finished photos',
  },
  detail: {
    /** *3 of 3 · Day 3 by Marco Diaz, Fri 3:41 PM*. */
    daysDone: (done: string, total: string, n: string, name: string, when: string) => `${done} of ${total} · Day ${n} by ${name}, ${when}`,
    /** *1 of 3 · Day 2 is on site now, Day 3 is tomorrow*. */
    daysOpen: (done: string, total: string, today: string, next: string, when: string) =>
      `${done} of ${total} · Day ${today} is on site now, Day ${next} is ${when}`,
    /** *no punch open · 0 flags · 1 edit, with its reason*. */
    timeDone: (flags: string, edits: string) => `no punch open · ${flags} flags · ${edits} edit, with its reason`,
    /** *3 on the clock now · no flags · 1 edit, with its reason*. */
    timeOpen: (onTheClock: string, edits: string) => `${onTheClock} on the clock now · no flags · ${edits} edit, with its reason`,
    /** *3 reviewed · Hilltop Hardware approved Thu, Sam paid back on the next payroll*. */
    receiptsDone: (n: string, vendor: string, day: string, name: string) =>
      `${n} reviewed · ${vendor} approved ${day}, ${name} paid back on the next payroll`,
    /** *1 to review — Hilltop Hardware, $64.52*. */
    receiptsOpen: (n: string, vendor: string, amount: string) => `${n} to review — ${vendor}, ${amount}`,
    review: COMMON.review,
    /** *both back · the mixer returned Fri 4:10 PM*. */
    rentalsDone: (noun: string, when: string) => `both back · the ${noun} returned ${when}`,
    /** *the excavator is back · the concrete mixer is booked for Friday*. */
    rentalsOpen: (back: string, booked: string, weekday: string) => `the ${back} is back · the ${booked} is booked for ${weekday}`,
    /** One leftover — *1 bag of poly sand* (with a unit) · *4 wall blocks* (without). */
    leftover: (qty: string, unit: string | null, noun: string) => (unit === null ? `${qty} ${noun}` : `${qty} ${unit} of ${noun}`),
    /** *1 bag of poly sand, 4 wall blocks · −$58.40 off materials* — the items from `leftover`. */
    leftoversDone: (items: readonly string[], amount: string) => `${COMMON.commaList(items)} · ${amount} off materials`,
    leftoversOpen: 'counted when the last day closes out',
    hoursDone: 'every unit, every day',
    /** *Day 1 in · Day 2 and Day 3 to come*. */
    hoursOpen: (done: readonly string[], toCome: readonly string[]) =>
      done.length === 1 && toCome.length === 2
        ? `Day ${lastOf(done)} in · Day ${toCome[0] ?? ''} and Day ${lastOf(toCome)} to come`
        : `${done.map(COMMON.dayN).join(', ')} in · ${COMMON.list(toCome.map(COMMON.dayN))} to come`,
    /** *4 tagged Finished*. */
    photosDone: (n: string) => `${n} tagged Finished`,
    photosOpen: 'none yet — asked for on the last day',
    /** *3 reviewed* — the receipts line with no pay-back to name. */
    reviewed: (n: string) => `${n} reviewed`,
    /** *1 to review — Hilltop Hardware* — without the amount, for a role without *Costs & margins*. */
    receiptsOpenShort: (n: string, vendor: string) => `${n} to review — ${vendor}`,
    /** *the mixer returned Fri 4:10 PM* — one rental back. */
    rentalReturned: (noun: string, when: string) => `the ${noun} returned ${when}`,
    /** *the excavator is back* · *the concrete mixer is booked for Friday* — each rental's part. */
    rentalBack: (noun: string) => `the ${noun} is back`,
    rentalBooked: (noun: string, weekday: string) => `the ${noun} is booked for ${weekday}`,
    /** The parts of a line, joined as the boards join them. */
    parts: (parts: readonly string[]) => parts.join(' · '),
    /** A flag still to look at (§11, `contractText`). */
    lookAtIt: TIME.lookAtIt,
  },
  final: {
    title: 'Final numbers',
    internal: COMMON.internal,
    head: {
      category: COMMON.category,
      estimate: COMMON.estimateLabel,
      actual: COMMON.actualLabel,
      vsEstimate: COMMON.vsEstimate,
    },
    /** §6's categories; *Subcontractors* only for a visit with sub lines (`contractText`). */
    categories: MONEY.categories,
    /** *· 76.0 → 78.0 h*. */
    laborHours: (estimated: string, actual: string) => `· ${estimated} → ${actual} h`,
    totalCost: COMMON.totalCost,
    margin: COMMON.marginLabel,
    /** *on $14,250*. */
    on: (price: string) => `on ${price}`,
  },
  /** *Tell Tom Reyes* — the cancel popup's words. */
  tell: CANCEL_VISIT.tell,
  /** *the install is done*. */
  tellSub: (noun: string) => `the ${noun} is done`,
  draftBalance: 'Draft the balance invoice',
  /** *Draft the completion invoice* — what the billing plan calls for when it is not a balance (`contractText`). */
  draftKind: (kind: string) => `Draft the ${kind} invoice`,
  /** *— $7,125.00* — with no deposit to name. */
  draftSub: (amount: string) => `— ${amount}`,
  /** *— $7,125.00 · the deposit on Q-2184 was paid Oct 12*. */
  draftBalanceSub: (amount: string, quote: string, date: string) => `— ${amount} · the deposit on ${quote} was paid ${date}`,
  aWord: 'A word on the visit',
  optional: ORDER.noted.optional,
  /** *Closing locks these numbers into J-3271. A fix after this is an adjustment, with a reason.* */
  footer: (job: string) => `Closing locks these numbers into ${job}. A fix after this is an adjustment, with a reason.`,
  footerOpen: 'The visit closes once every line passes.',
  cancel: COMMON.cancel,
  close: COMMON.close,
  submit: 'Close the visit',
  /** After — the job's *By visit* (Board 12 C). */
  byVisit: {
    /** *J-3271 · Money · By visit*. */
    title: (job: string) => `${job} · Money · By visit`,
    sub: 'every cost posts to a visit; the job is the sum',
    head: {
      visit: RAIL.about.visit,
      estH: 'Est h',
      actual: COMMON.actualLabel,
      labor: COMMON.labor,
      materials: TABS.materials,
      equipment: TABS.equipment,
      fees: COMMON.fees,
      gm: 'GM',
    },
    /** *V-1187 · Oct 21 – 23 · closed*. */
    lineClosed: RAIL.jobVisits.lineClosed,
    /** *V-1188 · 1 day · not scheduled · from Apr 15*. */
    lineUnscheduled: (reference: string, days: string, from: string) => `${reference} · ${days} · not scheduled · from ${from}`,
    oneDay: '1 day',
    /** *3 days* (Board 12 C's header). */
    nDays: (n: string) => `${n} days`,
  },
} as const

/** **Close out Day 1 — for the crew** — Board 18 D, §27.4. */
export const CLOSE_OUT_FOR_CREW = {
  /** *Close out Day 1 — for the crew*. */
  title: (n: string) => `Close out Day ${n} — for the crew`,
  /** *Wed Oct 21 · Hardscape 2 · Marco Diaz, lead*. */
  sub: (date: string, crew: string, lead: string) => `${date} · ${crew} · ${lead}, lead`,
  /** *Entered by Jen Hollis for Marco Diaz.* */
  enteredBy: (by: string, lead: string) => `Entered by ${by} for ${lead}.`,
  /** *The crew app can’t close out a day yet. When it can, Marco does this on the iPad and this form stays here for corrections.* */
  band: (name: string) =>
    `The crew app can’t close out a day yet. When it can, ${name} does this on the iPad and this form stays here for corrections.`,
  reachedOffice: 'How it reached the office',
  /** §27.2 `reached_by`. */
  reachedBy: {
    call: COMMON.call,
    text: COMMON.text,
    paper: 'Paper sheet',
    in_person: 'In person',
  },
  /** *Loaded for Day 1*. */
  loadedFor: (n: string) => `Loaded for Day ${n}`,
  head: {
    loaded: MATERIALS.state.loaded,
    used: MATERIALS.state.used,
    wasted: 'Wasted',
    returned: RENTALS.state.returned,
  },
  /** *from the yard · t*. */
  fromYard: (unit: string) => `from the yard · ${unit}`,
  addsUp: 'Both add up — used, wasted and returned equal what was loaded.',
  equipmentHours: COMMON.equipmentHours,
  aWord: 'A word on the day',
  internal: COMMON.internal,
  /** *Posts Day 1’s labor $925.75 and equipment $352.00 to V-1187*. */
  posts: (n: string, labor: string, equipment: string, reference: string) =>
    `Posts Day ${n}’s labor ${labor} and equipment ${equipment} to ${reference}`,
  /** *Day 1 then reads “closed out by Jen Hollis for Marco Diaz”.* */
  footer: (n: string, by: string, lead: string) => `Day ${n} then reads “closed out by ${by} for ${lead}”.`,
  cancel: COMMON.cancel,
  /** *Close out Day 1*. */
  submit: (n: string) => `Close out Day ${n}`,
  /** The unit after an equipment hours box. */
  unit: COMMON.hourUnit,
} as const

/** **Adjust** after close — §16.5; no board draws the popup, so its words are the boards' own (lead, round 2). */
export const ADJUST = {
  title: HEADER.closed.adjust,
  category: COMMON.category,
  categories: CLOSE.final.categories,
  amount: RECEIPTS.amount,
  why: COMMON.why,
  cancel: COMMON.cancel,
  submit: HEADER.closed.adjust,
} as const

// ===========================================================================
// The other states — Board 13 B, §17
// ===========================================================================

/** The header in each state, and the line under it. */
export const STATES = {
  /** *V-1188 · J-3271 · 1 day · 6.0 h · from Apr 15*. */
  unscheduledMeta: (reference: string, job: string, days: string, hours: string, from: string) =>
    `${reference} · ${job} · ${days} · ${hours} h · from ${from}`,
  oneDay: CLOSE.byVisit.oneDay,
  unscheduled: 'No days yet. The plan, materials and crew notes are there; Ready for… appears once a day is booked.',
  /** *Nothing on site yet. Ready for Monday leads the Overview; Review & close lists what is open.* */
  prep: (weekday: string) => `Nothing on site yet. Ready for ${weekday} leads the Overview; Review & close lists what is open.`,
  /** *drain pipe and fabric to order · stone to load*. */
  prepLine: (toOrder: readonly string[], toLoad: readonly string[]) =>
    toOrder.length > 1
      ? `${allButLast(toOrder)} and ${lastOf(toOrder)} to order · ${COMMON.list(toLoad)} to load`
      : `${lastOf(toOrder)} to order · ${COMMON.list(toLoad)} to load`,
  /** *Skipped by Kyle Brandt Jul 8: rain. Skipped visits cost nothing and bill nothing; the series counts it.* */
  skipped: (name: string, date: string, reason: string) =>
    `Skipped by ${`${name} ${date}`}: ${reason}. Skipped visits cost nothing and bill nothing; the series counts it.`,
  /** *was Mon Oct 19*. */
  was: (date: string) => `was ${date}`,
  /** *Held by Jen Hollis Oct 16: waiting on the borough’s backflow test. …* */
  onHold: (name: string, date: string, reason: string) =>
    `Held by ${`${name} ${date}`}: ${reason}. Its days came off the schedule; its holds at the yard were released.`,
  /** *Cancelled by Tom Reyes for the customer. Read-only; materials held for it went back to stock.* */
  cancelled: (name: string) => `Cancelled by ${name} for the customer. Read-only; materials held for it went back to stock.`,
} as const

/** A one-day visit — Board 13 A, §17.2. */
export const ONE_DAY = {
  title: COMMON.day,
  sub: 'a one-day visit draws one row',
  /** *7:30 – 4:00 · one day*. */
  when: (from: string, to: string) => `${from} – ${to} · one day`,
  ofPlanned: COMMON.ofPlanned,
} as const

/** **Billed on the actuals** — Board 13 A, §17.4 (time & materials). */
export const TM_BILLED = {
  title: 'Billed on the actuals',
  sub: 'time & materials — the invoice follows what happened, not the estimate',
  labor: COMMON.labor,
  /** *16.5 h × $58.00* · *40 × $1.10*. */
  times: (qty: string, each: string) => `${qty} × ${each}`,
  atCost: 'at cost',
  toInvoice: 'To invoice',
  /** *estimated $1,130.00*. */
  estimated: COMMON.estimated,
  openDraft: 'Open the invoice draft',
} as const

// ===========================================================================
// A recurring visit — Board 14, §18
// ===========================================================================

export const SERIES = {
  title: 'The series',
  /** *30 visits from Q-2101 · each one its own visit, numbers and all*. */
  sub: (n: string, quote: string) => `${n} visits from ${quote} · each one its own visit, numbers and all`,
  open: HEADER.secondary.open_series,
  /** *24 done*. */
  done: (n: string) => `${n} done`,
  /** *1 skipped · Jul 9, rain*. */
  skipped: (n: string, date: string, reason: string) => `${n} skipped · ${date}, ${reason}`,
  /** *today, 26*. */
  today: (n: string) => `today, ${n}`,
  /** *4 to go · ends Nov 19*. */
  toGo: (n: string, date: string) => `${n} to go · ends ${date}`,
  /** *‹ 25 · Thu Oct 15 · closed*. */
  previous: (n: string, date: string, state: string) => `‹ ${n} · ${date} · ${state}`,
  /** *27 · Thu Oct 29 ›*. */
  next: (n: string, date: string) => `${n} · ${date} ›`,
  /** **Hours per visit**. */
  hours: {
    title: 'Hours per visit',
    /** *estimate vs actual, visit by visit · average 6.4 h over 24 done*. */
    sub: (average: string, done: string) => `estimate vs actual, visit by visit · average ${average} h over ${done} done`,
    /** *6 of 24 over*. */
    over: (n: string, of: string) => `${n} of ${of} over`,
    /** *estimate 6.5 h*. */
    estimate: (hours: string) => `estimate ${hours} h`,
    legend: {
      atOrUnder: 'at or under',
      over: 'over',
      today: 'today, so far',
      skipped: 'skipped',
    },
  },
  /** The series' For the crew card. */
  editNote: 'Edit the note',
  /** *Weekly mowing · 26 of 30*. */
  name: (name: string, n: string, count: string) => `${name} · ${n} of ${count}`,
} as const

/** How far a change on a series visit goes — Board 14, §18. */
export const HOW_FAR = {
  crewNote: 'Change the crew note for',
  thisOnly: 'This visit only',
  /** *This one and the 4 after*. */
  thisAndAfter: (n: string) => `This one and the ${n} after`,
  /** *26 – 30 · through Nov 19*. */
  range: COMMON.through,
  closedNeverChange: 'Visits already closed never change.',
  cancel: COMMON.cancel,
  save: COMMON.save,
} as const

// ===========================================================================
// iPad and phone — Boards 15, 16, §19 (the iPad draws the desktop's words)
// ===========================================================================

export const PHONE = {
  back: HEADER.breadcrumb,
  /** *J-3271 · Whitmer Residence · Day 2 of 3*. */
  sub: (job: string, place: string, n: string, count: string) => `${job} · ${place} · Day ${n} of ${count}`,
  /** The four quick actions — *Call Marco · Navigate · Photo* and the green one by tab. */
  call: (name: string) => `Call ${name}`,
  navigate: COMMON.navigate,
  photo: 'Photo',
  green: {
    overview: COMMON.review,
    /** *Order · 1*. */
    materials: (n: string) => `Order · ${n}`,
    time: TIME.addTime,
    money: RECEIPTS.addShort,
    /** §19.2 (contractText): *Equipment **Rent**, For the crew **Add note**, From the crew and Files **Upload**, Activity and History **Log activity***, and Materials' *Add material* with none to order. */
    materialsNone: 'Add material',
    equipment: 'Rent',
    forCrew: CREW_NOTE.add,
    upload: FROM_CREW.upload,
    log: 'Log activity',
  },
  /** *Days · 3*. */
  days: (n: string) => `Days · ${n}`,
  add: 'Add',
  /** *of 27 · +1.0*. */
  ofPlannedDelta: (planned: string, delta: string) => `of ${planned} · ${delta}`,
  /** *of 76 · proj 77.0*. */
  ofProjected: (estimated: string, projected: string) => `of ${estimated} · proj ${projected}`,
  /** *proj $8,888*. */
  projected: (projected: string) => `proj ${projected}`,
  /** *est 37.8%*. */
  estimated: (estimated: string) => `est ${estimated}`,
  /** *Visits on J-3271 · 2*. */
  jobVisits: (job: string, n: string) => `Visits on ${job} · ${n}`,
  /** *For the crew · 9*. */
  forCrew: (n: string) => `For the crew · ${n}`,
  /** The bottom bar — Overview is the record shell's; *More* becomes *Close* while open. */
  bar: { materials: TABS.materials, time: TABS.time, money: TABS.money, more: DAY_SHEET.more, close: COMMON.close },
  moreTitle: 'More on this visit',
} as const

// ===========================================================================
// The visit's edit sheet — §7.3 (About *Edit*; *Edit plan* while unscheduled,
// Board 13 B; the phone's pencil, Board 16 A). Drawn on no board (lead, Oct
// 29: a DEVIATION REQUEST): About's own labels, and How far's on a series.
// ===========================================================================

export const EDIT_VISIT = {
  title: RAIL.about.title,
  visit: RAIL.about.visit,
  crew: RAIL.about.crew,
  leavesFrom: RAIL.about.leavesFrom,
  planned: RAIL.about.planned,
  unit: ADD_DAY.unit,
  /** *Hardscape 2 · Marco Diaz, lead*. */
  crewLead: RAIL.about.crewLead,
  /** The How far popover's heading for the visit itself (§D20 — *any edit of the plan, crew…*). */
  howFar: 'Change the visit for',
  cancel: COMMON.cancel,
  save: COMMON.save,
} as const

/**
 * ⋯ **Print the crew sheet** (§7.2 contractText; no board draws the sheet — a
 * DEVIATION REQUEST, lead Oct 29): the crew iPad's card and that day's load
 * list, printed from the browser. Its only words are the menu's own.
 */
export const CREW_SHEET = {
  print: HEADER.menu.print_crew_sheet,
  /** The tab's title — *V-1187 · Day 2*. */
  title: (reference: string, n: string) => COMMON.dot(reference, COMMON.dayN(n)),
} as const
